import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import moment from 'moment-timezone';
import update from 'immutability-helper';
import { useDataStore } from 'files/StateManagement.js';

import Abstract from 'classes/Abstract.js';
import ActionSteps from 'views/ActionSteps.js';
import { AddEditUser, ConfigureTriggers } from 'managers/Users.js';
import { AsyncInteractionItem } from 'managers/Components.js';
import { AltBadge } from 'views/Main.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper.js';
import AnimatedElement from 'views/AnimatedElement.js';
import API from 'files/api.js';
import Appearance from 'styles/Appearance.js';
import { CardElement, Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import BoolToggle from 'views/BoolToggle.js';
import Button from 'views/Button.js';
import Checkbox from 'views/Checkbox.js';
import CollapseContainer from 'views/CollapseContainer.js';
import FieldMapper from 'views/FieldMapper.js';
import Layer, { CollapseArrow, LayerItem, LayerItemSpacing } from 'structure/Layer.js';
import ListField from 'views/ListField.js';
import LottieView from 'views/Lottie.js';
import MultiToggle from 'views/MultiToggle.js';
import PageControl from 'views/PageControl.js';
import { PanelContentContainer } from 'structure/Panel.js';
import Payment from 'classes/Payment.js';
import ProgressBar from 'views/ProgressBar';
import Request from 'files/Request.js';
import Subscription from 'classes/Subscription.js';
import TextField from 'views/TextField.js';
import TextView from 'views/TextView.js';
import Utils from 'files/Utils.js';
import { ReceiptEuroIcon } from 'lucide-react';

export const AddPayoutDestinationCard = ({ onComplete }, { index, options, utils }) => {

    const stripeProps = useRef(null);

    const layerID = 'add_payout_destination_card';
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [newMethodCompleted, setNewMethodCompleted] = useState(false);
    const { user, setUser } = useDataStore();

    const onAddNewMethod = async () => {
        try {

            // prevent moving forward if method input is not complete
            if(newMethodCompleted === false) {
                throw new Error('Please fill out your card number, expiration date, and cvc code to continue.');
            }

            // set loading flag and prepare token for card
            setLoading('submit');
            let element = stripeProps.current.elements.getElement(CardElement);
            let { token } = await stripeProps.current.stripe.createToken(element, {currency: 'usd'});
            if(!token || !token.card) {
                throw new Error('Unable to tokenize card parameters');
            }

            // submit request to server
            let { destination, metadata } = await Request.post(utils, '/payments/', {
                category: 'debit_card',
                provider: 'stripe',
                token: token.id,
                type: 'new_payout_destination'
            });

            // end loading and clear payout destination field
            setLoading(false);
            element.clear();

            // notify subscribers that a new payout destination has been created
            utils.content.fetch('payout_destinations');

            // update user object with onboarding flag if applicable
            setUser(user => {
                user.onboarding.payouts.configured = true;
                user.onboarding.payouts.verified = metadata.verified;
                return user;
            });

            // notify listeners that a change has posted
            if(typeof(onComplete) === 'function') {
                onComplete(destination);
            }

            // show confirmation alert
            utils.banner.show({
                title: 'Debit Card Added',
                message: 'Your debit card has been added and is now available for future payouts.',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {

            // prepare error alert data for handler if applicable
            setLoading(false);
            if(e.code === Request.status.get().incomplete_user_profile) {
                return showEditProfileAlert(e.message);
            }
            utils.alert.error({
                error: e,
                message: `There was an issue adding your payout destination. ${e.message || 'An unknown error occurred'}`,
                origin: 'payments.add_payout_destination_card'
            });
        }
    }

    const onEditProfile = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users'
            }),
            Component: AddEditUser.bind(this, {
                isNewTarget: false
            }),
            id: `edit_user_${user.user_id}`
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onAddNewMethod,
            text: 'Submit'
        }];
    }

    const getDisclaimer = () => {
        return (
            <span style={{
                color: Appearance.colors.subText(),
                display: 'block',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.75,
                marginTop: 16,
                textAlign: 'left',
                width: '100%'
            }}>
                {`LifeWipe uses Stripe to securely manage payouts through its connected accounts platform. By adding a payout method and continuing with setup, you agree to be bound by the `}
                <span 
                className={'text-button'}
                onClick={window.open.bind(this, 'https://stripe.com/legal/connect-account')}
                style={{
                    color: Appearance.colors.primary(),
                    fontWeight: 600,
                    textDecoration: 'underline'
                }}>{'Stripe Connected Account Agreement'}</span>
                {`, which governs how funds are transferred, held, and disbursed. Stripe handles all payment processing in accordance with industry-leading compliance and security standards. Please review the terms before proceeding.`}
            </span>
        )
    }

    const getStripeCardComponent = () => {
        return (
            <LayerItem 
            lastItem={true}
            title={'Add New Payout Destination'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        paddingLeft: 16,
                        paddingRight: 16,
                        paddingTop: 12,
                        width: '100%'
                    }}>{`Add a debit card to your account to enable payouts. Only cards that support payouts in U.S. Dollars (USD) are currently accepted. Make sure the card details are accurate to avoid any issues.`}</span>
                    <div style={{
                        padding: 16,
                        width: '100%'
                    }}>
                        <TextField 
                        custom={true}
                        onClick={evt => evt.stopPropagation()}>
                            <Elements stripe={utils.stripe}>
                                <ElementsConsumer>
                                    {props => {
                                        stripeProps.current = props;
                                        return (
                                            <CardElement
                                            onChange={({ complete }) => setNewMethodCompleted(complete)}
                                            options={{
                                                hidePostalCode: true,
                                                style: {
                                                    invalid: {
                                                        color: Appearance.colors.red,
                                                    },
                                                    base: {
                                                        color: Appearance.colors.text(),
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        '::placeholder': {
                                                            color: Appearance.colors.subText(),
                                                        },
                                                        width: '100%'
                                                    }
                                                }
                                            }} />
                                        )
                                    }}
                                </ElementsConsumer>
                            </Elements>
                        </TextField>
                    </div>
                </div>
            </LayerItem>
        )
    }

    const showEditProfileAlert = message => {
        utils.alert.show({
            title: 'Just a Second',
            message: `There was an issue adding your payout destination. ${message}`,
            buttons: [{
                key: 'edit',
                title: 'Edit Profile',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Close',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'edit') {
                    onEditProfile();
                    return;
                }
            }
        });  
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}
        utils={utils}>
            {getStripeCardComponent()}
            {getDisclaimer()}
        </Layer>
    )
}

export const AddPayoutDestinationBankAccount = ({ onComplete }, { index, options, utils }) => {

    const stripeProps = useRef(null);

    const layerID = 'add_payout_destination_bank_account';
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [bankDetails, setBankDetails] = useState({
        routing_number: '',
        account_number: '',
        account_holder_name: ''
    });

    const onAddNewMethod = async () => {
        try {

            // prevent moving forward if method input is not complete
            if(!bankDetails.routing_number || !bankDetails.account_number || !bankDetails.account_holder_name) {
                throw new Error('Please fill out all bank account information to continue.');
            }

            // set loading flag and prepare token for bank account
            setLoading('submit');
            
            // For US bank accounts, we need to create a token using account and routing number
            // This would typically be collected via a custom form, not a Stripe element
            // For now, let's use a PaymentMethod approach
            let { paymentMethod, error } = await stripeProps.current.stripe.createPaymentMethod({
                billing_details: {
                    name: bankDetails.account_holder_name
                },
                type: 'us_bank_account',
                us_bank_account: {
                    account_holder_type: 'individual',
                    account_number: bankDetails.account_number,
                    account_type: 'checking',
                    routing_number: bankDetails.routing_number
                }
            });
            
            if(error || !paymentMethod) {
                throw new Error('Unable to create payment method for bank account');
            }

            // submit request to server
            let { destination } = await Request.post(utils, '/payments/', {
                category: 'bank_account',
                provider: 'stripe',
                payment_method_id: paymentMethod.id,
                type: 'new_payout_destination'
            });

            // end loading and clear form
            setLoading(false);
            setBankDetails({ routing_number: '', account_number: '', account_holder_name: '' });

            // notify subscribers that a new payout destination has been created
            utils.content.fetch('payout_destinations');

            // notify listeners that a change has posted
            if(typeof(onComplete) === 'function') {
                onComplete(destination);
            }

            // show confirmation alert
            utils.banner.show({
                title: 'Bank Account Added',
                message: 'Your new bank account payout destination has been added to your account.',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue adding your payout destination. ${e.message || 'An unknown error occurred'}`,
                origin: 'payments.add_payout_destination_card'
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onAddNewMethod,
            text: 'Submit'
        }];
    }

    const getDisclaimer = () => {
        return (
            <span style={{
                color: Appearance.colors.subText(),
                display: 'flex',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.75,
                marginTop: 24,
                textAlign: 'left',
                width: '100%'
            }}>{`LifeWipe Inc. uses Stripe, a leading global payment processor, to securely handle all financial transactions. When you add a bank account for payouts, your banking information is never stored on our servers — it is encrypted and processed directly through Stripe's PCI-DSS Level 1 certified platform (the highest level of payment security certification available).`}</span>
        )
    }

    const getFields = () => {
        return [{
            collapsed: null,
            description: `Add a bank account to your account to enable payouts. Only U.S. bank accounts that support ACH transfers are currently accepted. Make sure the account details are accurate to avoid any issues.`,
            key: 'bank_details',
            title: 'Bank Account Details',
            items: [{
                component: 'textfield',
                key: 'account_holder_name',
                onChange: text => setBankDetails(prev => ({ ...prev, account_holder_name: text })),
                title: 'Account Holder Name',
                value: bankDetails.account_holder_name
            },{
                component: 'textfield',
                key: 'routing_number',
                onChange: text => setBankDetails(prev => ({ ...prev, routing_number: text })),
                props: {
                    format: 'number'
                },
                title: 'Routing Number',
                value: bankDetails.routing_number
            },{
                component: 'textfield',
                key: 'account_number',
                onChange: text => setBankDetails(prev => ({ ...prev, account_number: text })),
                props: {
                    format: 'number'
                },
                title: 'Account Number',
                value: bankDetails.account_number
            }]
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}
        utils={utils}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            {getDisclaimer()}
        </Layer>
    )
}

export const AddEditPromotion = ({ isNewTarget, onChangeAsync, promotion }, { index, options, utils }) => {

    const layerID = isNewTarget ? 'new_promotion' : `edit_promotion_${promotion.id}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [edits, setEdits] = useState(promotion || {});

    const onDoneClick = async () => {
        try {

            // verify that variables were provided
            setLoading('done');
            await validateRequiredFields(getFields);

            // verify that start date is not after end date
            if(edits.end_date && edits.end_date < edits.start_date) {
                throw new Error('The end date must be after the start date');
            }

            // call async onChange handler and wait for a resolve before closing the layer
            // the handler validates the requested dates against previously requested dates
            await onChangeAsync(edits);
            setLayerState('close');

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue saving your changes. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_edit_promotion.on_done_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            text: 'Done'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'details',
            title: 'Promotion Details',
            items: [{
                component: 'textfield',
                key: 'title',
                onChange: text => onUpdateTarget({ title: text }),
                title: 'Title',
                value: edits.title
            },{
                component: 'textfield',
                key: 'amount',
                onChange: val => onUpdateTarget({ amount: val }),
                props: {
                    format: 'currency',
                    prepend: '$'
                },
                title: 'Cost',
                value: edits.amount || '0'
            },{
                component: 'date_picker',
                key: 'start_date',
                onChange: date => onUpdateTarget({ start_date: date }),
                props: { 
                    future: true 
                },
                title: 'Start Date',
                value: edits.start_date
            },{
                component: 'date_picker',
                key: 'end_date',
                onChange: date => onUpdateTarget({ end_date: date }),
                props: { 
                    future: true 
                },
                required: false,
                title: 'End Date',
                value: edits.end_date
            }]
        }];
    }

    useEffect(() => {
        setEdits({
            amount: '0.00',
            ...promotion
        });
    }, [])
    
    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'medium'
        }}
        utils={utils}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const AddPaymentMethod = ({ isPartnershipRequest = false, onChange, onCreateToken }, { index, options, utils }) => {

    const stripeProps = useRef(null);

    const layerID = 'add_payment_method';
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [newMethodCompleted, setNewMethodCompleted] = useState(false);

    const onAddNewMethod = async () => {
        try {

            // prevent moving forward if method input is not complete
            if(newMethodCompleted === false) {
                throw new Error('Please fill out your card number, expiration date, and cvc code to continue.');
            }

            // set loading flag and prepare token for card
            setLoading('submit');
            let element = stripeProps.current.elements.getElement(CardElement);
            let { token } = await stripeProps.current.stripe.createToken(element);
            if(!token || !token.card) {
                throw new Error('Unable to tokenize card parameters');
            }

            // return token as-is without any other logic if applicable
            if(typeof(onCreateToken)=== 'function') {
                onCreateToken(token);
                setLayerState('close');
                return;
            }

            // submit request to server
            let { payment_method } = await Request.post(utils, '/payments/', {
                is_partnership_request: isPartnershipRequest,
                source_id: token.card.id,
                token: token.id,
                type: 'new_payment_method'
            });

            // end loading and clear payment method field
            setLoading(false);
            element.clear();

            // notify subscribers that a new payment method has been created
            utils.content.fetch('payment_methods');

            // log firebase recommended add_payment_info event
            utils.analytics.log('add_payment_info', { payment_type: 'card' });

            // notify listeners that a change has posted
            if(typeof(onChange) === 'function') {
                onChange(Payment.Method.create(payment_method));
            }

            // show confirmation alert
            utils.banner.show({
                title: 'Payment Method Added',
                message: 'Your card has been added to your account and is now available for future purchases.',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue adding your payment method. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_payment_method.on_add_new_method'
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onAddNewMethod,
            text: 'Submit'
        }];
    }

    const getDisclaimer = () => {
        return (
            <span style={{
                color: Appearance.colors.subText(),
                display: 'flex',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.75,
                marginTop: 24,
                textAlign: 'left',
                width: '100%'
            }}>{`LifeWipe Inc. uses Stripe, a leading global payment processor, to securely handle all financial transactions. When you subscribe to LifeWipe services, your payment information is never stored on our servers — it is encrypted and processed directly through Stripe’s PCI-DSS Level 1 certified platform (the highest level of payment security certification available).`}</span>
        )
    }

    const getStripeCardComponent = () => {
        return (
            <LayerItem 
            lastItem={true}
            title={'Add New Payment Method'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: 16
                }}>
                    <Elements stripe={utils.stripe}>
                        <ElementsConsumer>
                            {props => {
                                stripeProps.current = props;
                                return (
                                    <CardElement
                                    onChange={({ complete }) => setNewMethodCompleted(complete)}
                                    options={{
                                        hidePostalCode: true,
                                        style: {
                                            invalid: {
                                                color: Appearance.colors.red,
                                            },
                                            base: {
                                                color: Appearance.colors.text(),
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                '::placeholder': {
                                                    color: Appearance.colors.subText(),
                                                },
                                            }
                                        }
                                    }} />
                                )
                            }}
                        </ElementsConsumer>
                    </Elements>
                </div>
            </LayerItem>
        )
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}
        utils={utils}>
            {getStripeCardComponent()}
            {getDisclaimer()}
        </Layer>
    )
}

export const ConfigurePayoutDestinations = ({ index, options, utils }) => {

    const layerID = 'configure_payout_destinations';
    const [destinations, setDestinations] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { user, setUser } = useDataStore();

    const onAddNewBankAccountClick = () => {
        utils.layer.open({
            Component: AddPayoutDestinationBankAccount.bind(this, {
                onComplete: result => {
                    setDestinations(destinations => {
                        destinations.push(result);
                        return destinations.sort((a,b) => a.name.localeCompare(b.name));
                    });
                }
            }),
            id: 'add_payout_destination_bank_account'
        });
    }

    const onAddNewDebitCardClick = () => {
        utils.layer.open({
            Component: AddPayoutDestinationCard.bind(this, {
                onComplete: result => {
                    setDestinations(destinations => {
                        destinations.push(result);
                        return destinations.sort((a,b) => a.name.localeCompare(b.name));
                    });
                }
            }),
            id: 'add_payout_destination_card'
        });
    }

    const onDestinationClick = destination => {
        
    }

    const onStripeVerificationClick = () => {
        return new Promise((resolve, reject) => {

            const onConfirm = async result => {
                try {

                    // submit request to server
                    let { verified } = await Request.post(utils, '/payments/', {
                        ssn_last_4: result,
                        type: 'update_payouts_account_ssn'
                    });

                    // update user object with verification flag
                    setUser(user => {
                        user.onboarding.payouts.verified = verified;
                        return user;
                    });

                    // show confirmation alert if verification was successful
                    if(verified === true) {
                        resolve({ message: 'Thank you for verifying your account. No further action is required at this time.'})
                        return;
                    }
                    
                    // throw an error indicating that additional verifications are required
                    throw new Error('Additional verifications are required to complete your payouts account setup. Please contact support for more information.');

                } catch(e) {
                    reject(e);
                }
            }

            // show alert requesting details from user
            let result = null;
            utils.alert.show({
                title: 'Verification',
                message: `Please provide the last 4 digits of your Social Security Number below to continue.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        isSecure={true}
                        onChange={text => result = text}
                        placeholder={'SSN Last 4'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Submit',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(result && key === 'confirm') {
                        onConfirm(result);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        })
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getComplianceComponents = () => {
        const { configured = false, verified = false } = user.onboarding.payouts || {};
        return configured === true && verified === false && (
            <LayerItem 
            collapsed={false}
            title={'Information Required'}
            titleStyle={{
                color: Appearance.colors.red
            }}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: '12px 16px 16px 16px',
                    }}>{`To comply with payment regulations, you’ll need to provide the last 4 digits of your Social Security Number once your account reaches $3,000 in revenue. You don’t need to take action yet, but our payment processor will require this info before any payouts beyond that threshold can be processed.`}</span>
                    <AsyncInteractionItem 
                    description={'Provide the last 4 digits of your Social Security Number to keep your account in good standing.'}
                    image={'images/stripe-verification-yellow.png'}
                    onAsyncClick={onStripeVerificationClick}
                    title={'Begin Verification'}
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'Stripe, our payment processor, outlines how your data is handled in their privacy policy. Review the policy if you have questions or concerns.'}
                    image={'images/stripe-logo-purple.png'}
                    lastItem={true}
                    onAsyncClick={window.open.bind(this, 'https://stripe.com/privacy')}
                    title={'Stripe Privacy Policy'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getContent = () => {
        return (
            <>
            {getComplianceComponents()}
            <LayerItem 
            collapsed={false}
            title={'My Destinations'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: '12px 16px 16px 16px',
                    }}>{`When commissions are processed, your earnings are sent to your default payout destination, such as a debit card or bank account. You can add, remove, or update your payout destinations anytime to make sure funds go exactly where you want them.`}</span>
                    {getPayouts()}
                </div>
            </LayerItem>
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'This will allow you to add a new bank account as a payout destination for your earnings.'}
                    image={'images/add-payout-destination-bank-account.png'}
                    onClick={onAddNewBankAccountClick}
                    title={'Add Bank Account'}
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'This will allow you to add a new debit card as a payout destination for your earnings.'}
                    image={'images/add-payout-destination-debit-card.png'}
                    lastItem={true}
                    onClick={onAddNewDebitCardClick}
                    title={'Add Debit Card'}
                    utils={utils} />
                </div>
            </LayerItem>
            </>
        )
    }

    const getPayouts = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 24,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(destinations.length === 0) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payout-destination-dark.png' : 'images/payout-destination-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                            fontFamily: 'Zen Dots'
                        }}>{('No Destinations Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            whiteSpace: 'normal'
                        }}>{'No payout destinations were found for your account.'}</span>
                    </div>
                </div>
            )
        }
        return destinations.map((destination, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onDestinationClick.bind(this, destination)}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payout-destination-dark.png' : 'images/payout-destination-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                            fontFamily: 'Zen Dots'
                        }}>{destination.name.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            whiteSpace: 'normal'
                        }}>{`Created ${Utils.format.date(destination.date)}`}</span>
                    </div>
                    {destination.is_default === true && (
                        <AltBadge content={{
                            color: Appearance.colors.primary(),
                            text: 'Default'
                        }} />
                    )}
                    {destination.is_default === false && (
                        <img
                        src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                        style={{
                            height: 20,
                            objectFit: 'contain',
                            opacity: 1,
                            width: 15
                        }} />
                    )}
                </div>
            )
        });
    }

    const fetchDestinations = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { destinations } = await Request.get(utils, '/payments/', {
                type: 'payout_destinations'
            });

            // end loading and update local state
            setLoading(false);
            setDestinations(destinations.map(dest => ({
                ...dest, 
                date: moment.utc(dest.date).local()
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your destinations list. ${e.message || 'An unknown error occurred'}`,
                origin: 'configure_payout_destinations.fetch_destinations'
            });
        }
    }

    useEffect(() => {
        fetchDestinations();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            {getContent()}
        </Layer>
    )
}

export const EditPaymentMethod = ({ abstract, index, options, utils }) => {
    
    const layerID = `edit_payment_method_${abstract.getID()}`;
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onDeletePaymentMethodClick = () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {
        
                    // submit request to server
                    await Request.post(utils, '/payments/', {
                        id: abstract.getID(),
                        type: 'delete_payment_method'
                    });

                    // notify subscribers of data change
                    utils.content.fetch('payment_methods');

                    // notify origin that promise has been resolved
                    resolve({
                        message: 'This payment method has been deleted from your account.',
                        onClick: setLayerState.bind(this, 'close')
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue removing this payment method from your account. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Delete Payment Method',
                message: `Are you sure that you want to delete this payment method from your account?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onSubmitClick = async () => {
        try {
            setLoading('submit');
            await validateRequiredFields(getFields);
            await Request.post(utils, '/payments/', {
                ...edits,
                id: abstract.getID(),
                type: 'update_payment_method'
            });

            // update target values and notify subscribers of data change
            abstract.object.name = edits.name;
            utils.content.update(abstract);
            
            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Payment Method Updated',
                message: `Your "${edits.name}" payment method has been updated.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue updating your payment method. ${e.message || 'An unknown error occurred'}`,
                origin: 'edit_payment_method.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(prev => ({ ...prev, ...props }));
    }

    const getAccessManagementComponents = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Access Management'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'This will prevent this payment method from being used for future payments unless manually added back to your account.'}
                    image={'images/delete-red-small.png'}
                    lastItem={true}
                    onAsyncClick={onDeletePaymentMethodClick}
                    title={'Delete from Account'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }


    const getFields = () => {
        return [{
            key: 'details',
            lastItem: false,
            title: 'Payment Method Details',
            items: [{
                component: 'textfield',
                key: 'name',
                onChange: text => onUpdateTarget({ name: text }),
                title: 'Name',
                value: edits.name
            }]
        }]
    }
    
    const setupEditing = () => {
        setEdits({ name: abstract.object.name });
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}
        utils={utils}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            {getAccessManagementComponents()}
        </Layer>
    )
}

export const EmergencyTriggers = ({ utils }) => {

    const limit = 5;
    const offset = useRef(0);

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const { user } = useDataStore();

    const onEmergencyTriggerClick = entry => {
        utils.alert.dev();
        return;
        utils.layer.open({  
            abstract: Abstract.create({
                object: entry,
                type: 'emergency_triggers'
            }),
            Component: EmergencyTriggerDetails,
            id: `emergency_triggers.${entry.id}`
        });
    }

    const getEntries = () => {
        if(entries.length === 0) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    borderWidth: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={`images/emergency-trigger-${window.theme}.png`}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{('No Activations Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            whiteSpace: 'normal'
                        }}>{'We were unable to locate any Emergency Trigger activations for your account.'}</span>
                    </div>
                </div>
            )
        }
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {entries.map((entry, index) => {
                    return (
                        <div 
                        className={`view-entry ${window.theme}`}
                        key={index}
                        onClick={onEmergencyTriggerClick.bind(this, entry)}
                        style={{
                            alignItems: 'center',
                            borderBottom: index !== entries.length - 1 && Appearance.styles.bottomBorder(),
                            display: 'flex',
                            flexDirection: 'row',
                            minWidth: 0,
                            padding: 16,
                            width: '100%'
                        }}>
                            <img 
                            className={'d-none d-xl-flex'}
                            src={`images/emergency-trigger-${window.theme}.png`}
                            style={{
                                height: 50,
                                marginRight: 16,
                                objectFit: 'contain',
                                width: 50
                            }} />
                            <div style={{
                                alignItems: 'flex-start',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.header(),
                                    fontFamily: 'Zen Dots'
                                }}>{entry.date.format('MMMM Do, YYYY').toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    color: Appearance.colors.subText()
                                }}>{`Activated ${entry.date.format('[at] h:mma')}`}</span>
                            </div>
                            <img
                            src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                            style={{
                                height: 20,
                                objectFit: 'contain',
                                opacity: 1,
                                width: 15
                            }} />
                        </div>
                    )
                })}
            </div>
        )
    }

    const fetchEntries = async () => {
        try {
            
            let { emergency_triggers, paging } = await Request.get(utils, '/subscriptions/', {
                limit: limit,
                offset: offset.current,
                type: 'emergency_triggers'
            });

            setLoading(false);
            setEntries(emergency_triggers.map(entry => Subscription.EmergencyTrigger.create(entry)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your Emergency Triggers history list. ${e.message || 'An unknown error occurred'}`,
                origin: 'emergency_trigger_history.fetch_entries'
            });
        }
    }

    useEffect(() => {
        user && fetchEntries();
    }, [user]);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`The list below shows all the Emergency Triggers that have been activated for your account. This comprehensive log helps you keep track of your digital legacy actions and monitor the status of each emergency trigger that you or your trusted contacts have initiated. You can configure your Emergency Trigger settings—like contacts, and instructions—by clicking the button below. It’s your plan, your rules.`}
                loading={loading}
                title={'Activation History'}>
                    {getEntries()}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchEntries();
                        }}
                        style={{
                            borderTop: null,
                            marginTop: 16
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const EmergencyTriggerDetails = ({ abstract, index, options, utils }) => {
    
    const resource = {
        category: 'emergency_triggers',
        id: `emergency_triggers.${abstract.getID()}`,
        target: abstract.object,
        title: `Emergency Trigger Audit Details`
    }

    const [layerState, setLayerState] = useState(null); 
    const { requiem } = useDataStore();

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    } 

    const getFields = () => {
        return [{
            id: 'details',
            title: 'Details',
            items: [{
                id: 'date',
                title: 'Created',
                value: Utils.format.date(abstract.object.date)
            },{
                id: 'id',
                title: 'ID',
                value: `LC${abstract.getID()}`
            },{
                id: 'status',
                title: 'Status',
                value: abstract.object.status?.text
            }]
        }];
    }

    useEffect(() => {
        return () => {
            requiem.functions.smart_flow.cancel(resource);
        }
    }, []);

    return (
        <Layer 
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            <FieldMapper 
            sections={getFields()}
            utils={utils} />
        </Layer>
    )
}

const getSpecialtyPaymentMethods = () => {
    return [{
        brand: {
            description: 'Apple Wallet (Card or Apple Cash)',
            name: 'Apple',
            image_url: 'images/payment-method-apple-pay.png'
        },
        id: 'apple_pay',
        name: 'Apple Pay',
        type: 'digital_wallet'
    },{
        brand: {
            description: 'Cash App Balance or Linked Card',
            name: 'Cash App',
            image_url: 'images/payment-method-cash-app-pay.png'
        },
        id: 'cash_app',
        name: 'Cash App Pay',
        type: 'external_service'
    },{
        brand: {
            name: 'Google',
            image_url: 'images/payment-method-google-pay.png'
        },
        id: 'google_pay',
        name: 'Google Pay',
        type: 'digital_wallet',
        visible: false
    },{
        brand: {
            name: 'Klarna',
            image_url: 'images/payment-method-financing-klarna.png'
        },
        id: 'klarna',
        name: 'Klarna',
        type: 'financing'
    }].filter(item => {
        return item.visible !== false;
    });
}

export const LifeChecks = ({ utils }) => {

    const limit = 5;
    const offset = useRef(0);

    const [loading, setLoading] = useState(true);
    const [lifeChecks, setLifeChecks] = useState([]);
    const [paging, setPaging] = useState(null);
    const { requiem, user, setUser, setRequiem } = useDataStore();

    const onConfigureLifeChecks = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('life_checks', 'create');

            // show layer with options to configure triggers
            utils.layer.open({
                abstract: Abstract.create({
                    object: user.subscription,
                    type: 'subscriptions'
                }),
                Component: ConfigureTriggers.bind(this, { category: 'life_checks' }),
                id: `life_checks.${user.subscription.id}`
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onLifeCheckClick = lifeCheck => {
        utils.layer.open({  
            abstract: Abstract.create({
                object: lifeCheck,
                type: 'life_checks'
            }),
            Component: LifeCheckDetails,
            id: `life_checks.${lifeCheck.id}`
        });
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'configure_lifechecks',
            onClick: onConfigureLifeChecks,
            text: 'Configure LifeChecks',
            visible: user?.subscription ? true : false
        }];
    }

    const getDescription = lifeCheck => {
        if(lifeCheck.completion_date) {
            return `Completed ${lifeCheck.completion_date.format('[at] h:mma')}`;
        }
        if(lifeCheck.failure_date) {
            return `Failed ${lifeCheck.failure_date.format('[at] h:mma')}`;
        }
        return `Awaiting verification...`;
    }

    const getLifeChecks = () => {
        if(lifeChecks.length === 0) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    borderWidth: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/life-check-dark.png' : 'images/life-check-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{('No LifeChecks Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            whiteSpace: 'normal'
                        }}>{'We were unable to locate any LifeChecks for your account.'}</span>
                    </div>
                </div>
            )
        }
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {lifeChecks.map((lifeCheck, index) => {
                    return (
                        <div 
                        className={`view-entry ${window.theme}`}
                        key={index}
                        onClick={onLifeCheckClick.bind(this, lifeCheck)}
                        style={{
                            alignItems: 'center',
                            borderBottom: index !== lifeChecks.length - 1 && Appearance.styles.bottomBorder(),
                            display: 'flex',
                            flexDirection: 'row',
                            minWidth: 0,
                            padding: 16,
                            width: '100%'
                        }}>
                            <img 
                            className={'d-none d-xl-flex'}
                            src={lifeCheck.is_emergency_override ? `images/life-check-emergency-override-${window.theme}.png` : `images/life-check-${window.theme}.png`}
                            style={{
                                height: 50,
                                marginRight: 16,
                                objectFit: 'contain',
                                width: 50
                            }} />
                            <div style={{
                                alignItems: 'flex-start',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.header(),
                                    fontFamily: 'Zen Dots',
                                    ...lifeCheck.is_emergency_override && {
                                        color: Appearance.colors.red
                                    }
                                }}>{(lifeCheck.is_emergency_override ? 'Emergency Override' : lifeCheck.date.format('MMMM Do, YYYY')).toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    color: Appearance.colors.subText()
                                }}>{getDescription(lifeCheck)}</span>
                            </div>
                            {lifeCheck.completion_date !== null && (
                                <AltBadge 
                                className={'d-none d-xl-flex'}
                                content={{
                                    color: Appearance.colors.primary(),
                                    text: 'Completed'
                                }} />
                            )}
                            {lifeCheck.failure_date !== null && (
                                <AltBadge 
                                className={'d-none d-xl-flex'}
                                content={{
                                    color: Appearance.colors.red,
                                    text: 'Failed'
                                }} />
                            )}
                            <img
                            src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                            style={{
                                height: 20,
                                objectFit: 'contain',
                                opacity: 1,
                                width: 15
                            }} />
                        </div>
                    )
                })}
            </div>
        )
    }

    const fetchLifeChecks = async () => {
        try {
            
            let { life_checks, paging } = await Request.get(utils, '/subscriptions/', {
                limit: limit,
                offset: offset.current,
                type: 'life_checks'
            });

            setLoading(false);
            setLifeChecks(life_checks.map(lc => Subscription.LifeCheck.create(lc)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your LifeChecks list. ${e.message || 'An unknown error occurred'}`,
                origin: 'life_checks.fetch_life_checks'
            });
        }
    }

    const onProcessRequiemNavigationRequest = () => {

        // no further action is needed if there is no target match
        if(requiem.action?.target !== 'lifechecks') {
            return;
        }

        // determine next steps for the action request
        switch(requiem.action?.key) {
            case 'configure_lifechecks':
            onConfigureLifeChecks();
            break;
        }

        // reset requiem object for next request
        setRequiem({ ...requiem, action: null });
    }

    const onProcessRequiemReviewRequest = async () => {
        try {

            // no further action is needed if there is no target match
            if(requiem.review?.category !== 'lifechecks') {
                return;
            }

            // fetch details for subscription from server and update user object with changes
            let subscription = await Subscription.get(utils, requiem.review.id);
            setUser(user => {
                user.onboarding.has_subscription = true;
                user.subscription = subscription;
                return user;
            });

            // open configuration for lifechecks after a brief pause while the state updates
            setTimeout(onConfigureLifeChecks, 500);

            // reset requiem object for next request
            setRequiem({ ...requiem, review: null });

        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        onProcessRequiemReviewRequest();
        onProcessRequiemNavigationRequest();
    }, [requiem]);

    useEffect(() => {
        fetchLifeChecks();
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`LifeChecks are periodic check-ins designed to confirm you're safe and in control. If you miss too many, your pre-set instructions may activate — including notifying trusted contacts, locking accounts, or beginning data handoff. This feature is preventative, not reactive. Use it only if you're ready to automate responses in case you're ever unreachable. You're still in charge — but now you have a safety net.`}
                buttons={getContainerButtons()}
                leftContent={(
                    <video
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        width: '100%'
                    }}>
                        <source 
                        src={"images/life-checks.mp4"}
                        type={"video/mp4"} />
                    </video>
                )}
                title={'LifeChecks'} />
            </AnimatedElement>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`The list below shows all the LifeChecks that have been completed for your account, including those that failed, those still pending, and those that were successfully completed. This comprehensive log helps you keep track of your digital legacy actions and monitor the status of each lifecheck that you or your trusted contacts have initiated. You can configure your LifeCheck settings—like timing, contacts, and instructions—by clicking the button below. It’s your plan, your rules.`}
                loading={loading}
                title={'Check-In History'}>
                    {getLifeChecks()}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchLifeChecks();
                        }}
                        style={{
                            borderTop: null,
                            marginTop: 16
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const LifeCheckDetails = ({ abstract, index, options, utils }) => {
    
    const resource = {
        category: 'life_checks',
        id: `life_checks.${abstract.getID()}`,
        target: abstract.object,
        title: `LifeCheck Audit Details`
    }

    const [layerState, setLayerState] = useState(null); 
    const { requiem } = useDataStore();

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getFields = () => {
        return [{
            id: 'details',
            title: 'Details',
            items: [{
                id: 'date',
                title: 'Created',
                value: Utils.format.date(abstract.object.date)
            },{
                color: abstract.object.completion_date && Appearance.colors.green,
                id: 'completion_date',
                title: 'Completion Date',
                value: abstract.object.completion_date && Utils.format.date(abstract.object.completion_date),
                visible: abstract.object.completion_date ? true : false
            },{
                color: abstract.object.is_emergency_override && Appearance.colors.red,
                id: 'is_emergency_override',
                title: 'Emergency Override',
                value: abstract.object.is_emergency_override ? 'Yes' : 'No',
                visible: abstract.object.is_emergency_override
            },{
                color: abstract.object.failure_date && Appearance.colors.red,
                id: 'failure_date',
                title: 'Failure Date',
                value: abstract.object.failure_date && Utils.format.date(abstract.object.failure_date),
                visible: abstract.object.failure_date ? true : false
            },{
                id: 'id',
                title: 'ID',
                value: `LC${abstract.getID()}`
            },{
                id: 'status',
                title: 'Status',
                value: abstract.object.status?.text
            }]
        }];
    }

    useEffect(() => {
        return () => {
            requiem.functions.smart_flow.cancel(resource);
        }
    }, []);

    return (
        <Layer 
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            <FieldMapper 
            sections={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const MyPayouts = ({ utils }) => {
   
    const limit = 10;
    const offset = useRef(0);

    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const [payouts, setPayouts] = useState([]);
    const { sockets } = useDataStore();

    const onConfigurePayoutsClick = () => {
        utils.layer.open({
            Component: ConfigurePayoutDestinations,
            id: 'configure_payout_destinations'
        });
    }

    const onNewPayoutEvent = evt => {
        offset.current = 0;
        fetchPayouts();
    }

    const onPayoutClick = payout => {
        utils.layer.open({
            abstract: Abstract.create({
                object: payout,
                type: 'payouts'
            }),
            Component: PayoutDetails,
            id: `payouts.${payout.id}`
        });
    }

    const onPayoutStatusChange = evt => {
        try {
            setPayouts(payouts => {
                return payouts.map(payout => {
                    if(payout.id === evt.id) {
                        payout.status = evt.status;
                    }
                    return payout;
                })
            })

        } catch(e) {
            console.error(e.message);
        }
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'configure',
            onClick: onConfigurePayoutsClick,
            text: 'Configure Payouts'
        }];
    }

    const getPayouts = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(payouts.length === 0) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payouts-dark.png' : 'images/payouts-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{('No Payouts Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            whiteSpace: 'normal'
                        }}>{'No payouts have been created for your account.'}</span>
                    </div>
                </div>
            )
        }
        return payouts.map((payout, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onPayoutClick.bind(this, payout)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== payouts.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    src={window.theme === 'dark' ? 'images/payouts-dark.png' : 'images/payouts-light.png'}
                    style={{
                        borderRadius: 8,
                        height: Utils.isMobile() ? 40 : 55,
                        flex: 'none',
                        marginRight: 12,
                        objectFit: 'cover',
                        width: Utils.isMobile() ? 40 : 55
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0,
                        textAlign: 'left'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{Utils.toCurrency(payout.amount).toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText({ color: Appearance.colors.subText() }),
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{Utils.format.date(payout.date)}</span>
                    </div>
                    <AltBadge content={payout.status} />
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 20,
                        flex: 'none',
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const connectToSockets = () => {
        sockets.on('payments', 'new_payout', onNewPayoutEvent);
        sockets.on('payments', 'payout_status_change', onPayoutStatusChange);
        return () => {
            sockets.off('payments', 'new_payout', onNewPayoutEvent);
            sockets.off('payments', 'payout_status_change', onPayoutStatusChange);
        }
    }

    const fetchPayouts = async () => {
        try {
            let { paging, payouts } = await Request.get(utils, '/payments/', {
                limit: limit,
                offset: offset.current,
                type: 'payouts'
            });

            setLoading(false);
            setPaging(paging);
            setPayouts(payouts.map(payout => ({
                ...payout,
                date: moment.utc(payout.date).local()
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your payouts list. ${e.message || 'An unknown error occurred'}`,
                origin: 'payouts.fetch_payouts'
            });
        }
    }

    useEffect(() => {

        fetchPayouts();
        let disconnect = connectToSockets();
        utils.content.subscribe('payouts_list', ['payouts'], {
            onFetch: fetchPayouts
        });
        return () => {
            disconnect();
            utils.content.unsubscribe('payouts_list'); 
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`This section provides a detailed history of the commissions you've earned through the LifeWipe platform. Payouts are generated when a sale is made either directly by you or by a partner connected to your referral network. Each entry includes the source of the sale, the payout amount, and the date it was credited to your account. These records reflect finalized transactions and give you a transparent view of your ongoing earnings.`}
                buttons={getContainerButtons()}
                title={'My Payouts'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {getPayouts()}
                    </div>
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset}
                        onClick={next => {
                            offset.current = next;
                            fetchPayouts();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const NewSubscription = ({ defaultView, defaultSchedule, familyPlan = false, freeTrial = false, isActivationMode = false, isPartnershipRequest = false, onComplete, onSelectionComplete, paymentRequest, selection, showStarterPlan = true, token }, { index, options, utils }) => {

    const layerID = 'new_subscription';
    const promotionsRef = useRef([]);
    const requestProps = useRef({});

    const [costs, setCosts] = useState(null);
    const [financingEnabled, setFinancingEnabled] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(paymentRequest ? 'payment_request' : false);
    const { partnership, setUser } = useDataStore();
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [promotions, setPromotions] = useState([]);
    const { request, user } = useDataStore();
    const [schedule, setSchedule] = useState(defaultSchedule || Subscription.schedules.get().monthly);
    const [stripeIntent, setStripeIntent] = useState({});
    const [tier, setTier] = useState(selection);
    const [view, setView] = useState(defaultView || null);
    
    const onProcessExternalServiceRequest = async (props = {}) => {

        const { client_secret, redirect_url } = props;

        const onConfirm = async () => {
            try {

                let response = null;
                switch(paymentMethod.id) {
                    case 'cash_app': {
                        response = await utils.stripe.confirmCashappPayment(client_secret, {
                            payment_method: {
                                billing_details: {
                                    address: {
                                        country: user?.country || 'US',
                                    },
                                    email: user?.email_address
                                }
                            },
                            return_url: redirect_url
                        });
                    }
                    break;
                    
                    case 'klarna': {
                        response = await utils.stripe.confirmKlarnaPayment(client_secret, {
                            payment_method: {
                                billing_details: {
                                    address: {
                                        country: user?.country || 'US',
                                    },
                                    email: user?.email_address
                                }
                            },
                            return_url: redirect_url
                        });
                    }
                    break;
                }

                console.log(response);
                
                // throw an error if an error was generated
                if(response?.error) {
                    throw new Error(response.error.message);
                }

                // show confirmation view if the intent was successful
                if(response.paymentIntent?.status === 'succeeded') {
                    setLayerState(null);
                    setView('subscription_confirmed');
                    return;
                }

                // reset layer state if the intent was cancelled
                if(response.paymentIntent?.status === 'canceled' || response.paymentIntent?.status === 'requires_action') {
                    setLayerState(null);
                }

            } catch(e) {
                setLoading(false);
                setLayerState(null);
                utils.alert.error({
                    error: e,
                    message: `There was an issue processing your payment. ${e.message || 'An unknown error occurred'}`,
                    origin: 'new_subscription.on_process_external_service_request'
                });
            }
        }

        utils.alert.show({
            title: `Pay with ${Utils.ucFirst(paymentMethod.brand.name)}`,
            message: `You'll be redirected to a secure application to complete your payment with ${Utils.ucFirst(paymentMethod.brand.name)}. After submitting your application, you'll be redirected back here to finalize your subscription.`,
            buttons: [{
                key: 'confirm',
                title: 'Continue',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onConfirm();
                    return;
                }
            }
        });
    }

    const onPromotionsChange = targets => {

        // update local state and ref with targets
        promotionsRef.current = targets;
        setPromotions(targets);

        // fetch new dynamic costs manually for promotions
        fetchDynamicCosts();
    }

    const onSetSelection = (selection, schedule) => {

        // track tier selection
        utils.analytics.log('subscription_tier_selected', {
            tier_id: selection.id,
            tier_name: selection.title
        });
        
        // set loading flag and update selection after brief pause
        setLoading('selection');
        setTimeout(() => {

            // update selection state and schedule state
            setTier(selection);
            setSchedule(schedule || Subscription.schedules.get().monthly);
            
            // end loading if selection is the stater plan
            if(selection.metadata.is_starter_plan === true) {
                setLoading(false);
            }
        }, 1000);
    }

    const onSubmitOrder = async props => {
        try {

            // prepare confirmation function call for post-payment selection
            const onConfirm = async payload => {

                // update request ref and set layer state for api request processing
                requestProps.current = payload;
                setLayerState('submit_api_request');
            }

            // prepare payload for server request
            let payload = {
                financing_enabled: financingEnabled,
                id: tier.id,
                is_partnership_request: isPartnershipRequest,
                override_confirmation: props?.override_confirmation,
                payment_method_id: paymentMethod?.id,
                payment_request_code: paymentRequest,
                promotions: promotions.map(promotion => promotion.id),
                schedule: schedule,
                token: token,
                two_factor_verification_code: props?.two_factor_verification_code,
                type: 'new'
            };

            // verify that a subscription has been selected before moving on
            if(!tier) {
                throw new Error('Please select a subscription before submitting your order');
            }

            // verify that a payment method has been selected before moving on
            if(!paymentMethod && isStarterPlan() === false) {
                throw new Error('Please select a payment method before submitting your order');
            }

            // determine if a free trial has been added and show disclaimer for retail customers
            if(costs?.post_trial_total && isPartnershipRequest === false) {
                await showFreeTrialDisclaimer();
            }

            // determine if payment method represents a digital wallet
            if(paymentMethod?.type === 'digital_wallet') {
                return newDigitalWalletPayment(result => {

                    // update loading flag
                    setLoading(false);

                    // remove payment intent listeners
                    stripeIntent.request.off('cancel');
                    stripeIntent.request.off('paymentmethod');

                    // show an error alert if applicable
                    if(result.error) {
                        if(result.error.message === 'user_cancelled') {
                            return;
                        }
                        utils.alert.error({
                            error: result.error,
                            message: `There was an issue submitting your order. ${result.error.message || 'An unknown error occurred'}`,
                            origin: 'subscription.submit_order'
                        });
                        return;
                    }

                    // submit result to server for subscription processing
                    onConfirm({ ...payload, ...result });
                });
            }

            // offload request to processor for non-digital wallet payment
            onConfirm(payload);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your order. ${e.message || 'An unknown error occurred'}`,
                origin: 'subscription.submit_order'
            });
        }
    }

    const onUpdateStripeIntent = async () => {
        try {

            // no further logic is required if no costs have been generated or payment method selected
            if(!costs || !paymentMethod) {
                return;
            }

            // no additional logic is required if the selected payment method is not supported
            console.log(paymentMethod.id);
            if(['apple_pay', 'google_pay'].includes(paymentMethod.id) === false) {
                return null;
            }

            // prepare display items from order overview (excluding total which is shown separately)
            const displayItems = getOrderOverviewItems().filter(item => {
                let amount = parseFloat(item.value.replace(/[$,]/g, ''));
                if(isNaN(amount) === true) {
                    return false;
                }
                return ['payment_method', 'total'].includes(item.key) === false;
            }).map(item => {
                return {
                    amount: parseFloat(item.value.replace(/[$,]/g, '')) * 100,
                    label: item.title
                }
            });

            // create payment request with only the selected wallet enabled
            const paymentRequest = utils.stripe.paymentRequest({
                country: user?.country || 'US',
                currency: 'usd',
                displayItems: displayItems,
                total: {
                    amount: costs?.total * 100,
                    label: 'LifeWipe Inc.'
                },
                requestPayerName: true,
                requestPayerEmail: true,
                wallets: paymentMethod?.id === 'apple_pay' ? ['applePay'] : ['googlePay']
            });

            // update local state with payment intent and capability details
            const canMakePayment = await paymentRequest.canMakePayment();
            setStripeIntent({
                capabilities: canMakePayment,
                request: paymentRequest
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const getApiRequestProps = () => {
        return {
            message: `We’re confirming your subscription and unlocking your account features. You’ll be ready to set up protections in just a moment.`,
            title: 'Preparing Your LifeWipe Plan',
            request: onProgressChange => {
                return new Promise(async (resolve, reject) => {
                
                    // register function to submit data to server
                    const onSubmit = async props => {
                        try {

                            // submit request to server
                            let response = await Request.post(utils, '/subscriptions/', {
                                ...requestProps.current,
                                ...props,
                                type: 'new'
                            },{
                                onProgressChange: onProgressChange
                            });

                            // determine if two factor verification is required
                            if(response.two_factor_verification_required) {
                                await Utils.sleep(0.5);
                                await processTwoFactorAuthenticationRequest(response.two_factor_verification_category, onSubmit);
                                return;
                            }

                            // prepare completion function call for component
                            resolve(() => {

                                // determine if an external payment option is selected and needs to be completed
                                if(paymentMethod?.type === 'financing' || paymentMethod?.type === 'external_service') {
                                    return onProcessExternalServiceRequest(response);
                                }

                                // end loading and notify subscribers that a new subscription has been created
                                utils.content.fetch('subscriptions');
                                
                                // track purchase with meta pixel
                                window.fbq('track', 'Purchase', {
                                    currency: 'USD',
                                    value: costs?.post_trial_total || costs?.base || 0
                                });

                                // track successful subscription purchase with firebase purchase event
                                utils.analytics.log('purchase', {
                                    currency: 'USD',
                                    items: [{
                                        item_id: tier.id,
                                        item_name: tier.title,
                                        item_category: 'Subscription',
                                        item_variant: Subscription.schedules.toText(schedule),
                                        price: costs?.post_trial_total || costs?.base || 0,
                                        quantity: 1
                                    }],
                                    shipping: 0,
                                    tax: 0,
                                    transaction_id: 'test',
                                    value: costs?.post_trial_total || costs?.base || 0
                                });
                                
                                // also track custom event for internal analytics
                                utils.analytics.log('subscription_purchase_completed', {
                                    has_promotions: promotions.length > 0,
                                    is_free_trial: costs?.post_trial_total ? true : false,
                                    is_partnership: isPartnershipRequest,
                                    schedule: Subscription.schedules.toText(schedule),
                                    subscription_id: response.subscription.id,
                                    tier_id: tier.id,
                                    tier_name: tier.title,
                                    total_cost: costs?.total || 0
                                });

                                // update user object with subscription
                                let target = Subscription.create(response.subscription);
                                setUser(user => {
                                    user.onboarding.has_subscription = true;
                                    user.subscription = target;
                                    return user;
                                });

                                // notify subscribers of general selection and close layer if applicable
                                if(typeof(onSelectionComplete) === 'function') {
                                    setLayerState('close');
                                    onSelectionComplete(target);
                                    return;
                                }

                                // notify subscribers of completion
                                if(typeof(onComplete) === 'function') {
                                    onComplete(target);
                                }

                                // determine if a partnership request is being processed
                                if(isPartnershipRequest) {
                                    utils.alert.show({
                                        title: 'All Done!',
                                        message: `The payment for the ${tier.title} subscription has been processed. ${partnership.user.first_name} will receive an email with the details for this transaction.`,
                                        buttons: [{
                                            key: 'cancel',
                                            title: 'Close',
                                            style: 'cancel'
                                        }],
                                        onClick: setLayerState.bind(this, 'close')
                                    });
                                    return;
                                }

                                // update layer state and view to confirmation screen
                                setLayerState(null);
                                setView('subscription_confirmed');
                            });

                        } catch(e) {

                            console.error(e.message);

                            // notify parent that promise failed and update view state
                            reject(e);
                            setLayerState(null);

                            // determine if a confirmation is required to cancel an existing subscription
                            if(e.code === Request.status.get().confirmation_required) {
                                return processSubscriptionUpgradeConfirmation(onSubmitOrder);
                            }
                            
                            // log user cancellation with firebase
                            if(e.message === 'user_cancelled') {
                                utils.analytics.log('subscription_signup_cancelled', {
                                    tier_id: tier?.id,
                                    tier_name: tier?.title,
                                    reason: 'user_cancelled'
                                });
                            }
                            
                            if(e.message !== 'user_cancelled') {

                                // log error with firebase
                                utils.analytics.log('subscription_signup_failed', {
                                    tier_id: tier?.id,
                                    tier_name: tier?.title,
                                    error: e.message
                                });

                                // show error alert to user
                                utils.alert.error({
                                    error: e,
                                    message: `There was an issue submitting your order. ${e.message || 'An unknown error occurred'}`,
                                    origin: 'subscription.submit_order'
                                });
                            }
                        }
                    }

                    // submit request to server
                    onSubmit();
                });
            }
        }
    }

    const getButtons = () => {
        if(loading === 'payment_request' || !tier) {
            return null;
        }
        if(view === 'subscription_confirmed') {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            }];
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitOrder,
            text: isStarterPlan() === true ? 'Start Plan' : 'Activate Plan',
            visible: tier ? true : false
        }];
    }

    const getContent = () => {
        if(loading === 'selection' || loading === 'payment_request') {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 48,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 55,
                        width: 55
                    }}/>
                </div>
            )
        }
        if(view === 'subscription_confirmed') {
            return (
                <>
                <LayerItem 
                collapsed={false}
                lastItem={false}
                title={'Congratulations!'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            display: 'block'
                        }}>
                            {`Welcome to LifeWipe — and congrats on taking control of your digital future. Your subscription is now active, and you’ve unlocked powerful tools to protect your data, your legacy, and the people you trust. Let’s get you set up — `}
                            <b>{`it only takes a few minutes`}</b>
                            {` to lock in your safeguards.`}
                        </span>
                    </div>
                </LayerItem>
                <ActionSteps
                lastItem={true}
                onClick={setLayerState.bind(this, 'close')}
                origin={'subscription_confirmation'}
                utils={utils} />
                </>
            )
        }
        if(!tier) {
            return (
                <SubscriptionOptions
                embedded={true}
                isActivationMode={isActivationMode}
                onSelectionClick={onSetSelection}
                showStarterPlan={shouldShowStarterPlan()} 
                utils={utils} />
            )
        }
        if(isStarterPlan() === true) {
            return (
                <>
                <LayerItem 
                collapsed={false}
                title={'Starter Plan Details'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        textAlign: 'left',
                        width: '100%'
                    }}>
                        {tier.benefits.filter(benefit => {
                            return benefit.value === null || benefit.value === false ? false : true;
                        }).map((benefit, index, benefits) => {
                            return (
                                <div 
                                key={index}
                                style={{
                                    alignItems: 'center',
                                    borderBottom: index !== benefits.length - 1 && Appearance.styles.bottomBorder(),
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    padding: 12,
                                    textAlign: 'left',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.defaultTextSmall()
                                    }}>{benefit.title}</span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultTextSmall()
                                    }}>{benefit.value}</span>
                                </div>
                            )
                        })}
                    </div>
                </LayerItem>
                <LayerItem 
                collapsed={false}
                lastItem={false}
                title={'Plan Overview'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {getOrderOverview()}
                    </div>
                </LayerItem>
                <span style={{
                    color: Appearance.colors.subText(),
                    display: 'flex',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.75,
                    textAlign: 'left',
                    width: '100%'
                }}>{`By submitting your signup, you agree to enroll in LifeWipe’s Starter Plan at no cost. This plan does not require payment and will not trigger any charges. If you choose to upgrade to a paid plan in the future, you’ll be asked to confirm your selection and authorize billing at that time. You can manage your subscription or upgrade anytime through your account settings.`}</span>
                </>
            )
        }
        return (
            <>
            {isActivationMode === false && (
                <LayerItem 
                collapsed={false}
                title={'Subscription Details'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        textAlign: 'left',
                        width: '100%'
                    }}>
                        {tier.benefits.filter(benefit => {
                            return benefit.value === null || benefit.value === false ? false : true;
                        }).map((benefit, index, benefits) => {
                            return (
                                <div 
                                key={index}
                                style={{
                                    alignItems: 'center',
                                    borderBottom: index !== benefits.length - 1 && Appearance.styles.bottomBorder(),
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    padding: 12,
                                    textAlign: 'left',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.defaultTextSmall()
                                    }}>{benefit.title}</span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultTextSmall()
                                    }}>{benefit.value}</span>
                                </div>
                            )
                        })}
                    </div>
                </LayerItem>
            )}
            {getPromotions()}
            <LayerItem 
            collapsed={false}
            title={'Payment Method'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <PaymentMethodSelector
                    financingEnabled={financingEnabled}
                    onChange={setPaymentMethod} 
                    isPartnershipRequest={isPartnershipRequest}
                    schedule={schedule}
                    utils={utils} 
                    value={paymentMethod} />
                </div>
            </LayerItem>
            <LayerItem 
            collapsed={false}
            lastItem={false}
            title={'Order Overview'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {getOrderOverview()}
                </div>
            </LayerItem>
            <span style={{
                color: Appearance.colors.subText(),
                display: 'flex',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.75,
                textAlign: 'left',
                width: '100%'
            }}>{`By submitting your subscription order, you authorize LifeWipe Inc. to charge your selected payment method for the subscription plan you’ve chosen, in the amount and frequency specified at checkout. This is a recurring billing agreement. Charges will continue automatically unless you cancel your subscription through your account settings or by contacting support. You may update your payment method or cancel at any time prior to your next billing date to avoid further charges.`}</span>
            </>
        )
    }

    const getLayerOptions = () => {
        if(loading === 'payment_request') {
            return {
                sizing: 'small'
            }
        }
        if(tier || loading === 'selection' || view === 'subscription_confirmed') {
            return {
                sizing: loading === 'selection' ? 'small' : 'medium'
            }
        }
        return {
            onCloseButtonClick: setLayerState.bind(this, 'close'),
            sizing: 'extra_large'
        }
    }

    const getOrderOverview = () => {
        
        // no additional logic is required if costs have not been calculated
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 12,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }

        return getOrderOverviewItems().map((item, index, items) => {
            return (
                <div 
                key={index}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== items.length - 1 && Appearance.styles.bottomBorder(),
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: 12,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                    }}>{item.title}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                        ...item.color && {
                            color: item.color
                        }
                    }}>{item.value}</span>
                </div>
            )
        })
    }

    const getOrderOverviewItems = () => {

        // return minimal fields for starter plan
        if(isStarterPlan() === true) {
            return [{
                key: 'billing',
                title: 'Billing Cycle',
                value: 'None'
            },{
                key: 'base_cost',
                title: 'Subscription Cost',
                value: Utils.toCurrency(0)
            },{
                color: Appearance.colors.primary(),
                key: 'total',
                title: 'Total',
                value: Utils.toCurrency(0)
            }];
        }
        
        // prepare billing items with dynamic costs
        let items = [{
            key: 'billing',
            title: 'Billing Cycle',
            value: Subscription.schedules.toText(schedule)
        },{
            key: 'payment_method',
            title: 'Payment Method',
            value: paymentMethod?.name || 'Not Selected',
            visible: paymentMethod ? true : false
        },{
            key: 'billing_start_date',
            title: 'First Payment Date',
            value: Utils.format.date(costs?.billing_start_date, { exclude_time: true}),
            visible: costs?.billing_start_date ? true : false
        },{
            key: 'renewal_date',
            title: 'Renewal Date',
            value: Utils.format.date(costs?.renewal_date, { exclude_time: true}),
            visible: isStarterPlan() === false
        },{
            key: 'base_cost',
            title: 'Subscription Cost',
            value: Utils.toCurrency(costs?.base ?? 0)
        }];

        // determine if at least one promotion is present
        if(costs?.promotions?.length > 0) {

            // add available promotions for service prior to sub-total
            costs.promotions.forEach(promotion => {
                if(promotion.category === Subscription.promotions.categories.get().service) {
                    items.push({
                        key: promotion.id,
                        title: promotion.title,
                        value: Utils.toCurrency(promotion.amount)
                    });
                }
            });

            // add sub-total line items 
            items.push({
                key: 'sub_total',
                title: 'Sub-Total',
                value: Utils.toCurrency(costs?.sub_total ?? 0)
            });

            // add available promotions for discount after sub-total
            costs.promotions.forEach(promotion => {
                if(promotion.category !== Subscription.promotions.categories.get().service) {
                    items.push({
                        color: Appearance.colors.gold,
                        key: promotion.id,
                        title: `${promotion.title} Promotion`,
                        value: `-${Utils.toCurrency(promotion.amount)}`
                    });
                }
            });
        }

        // add total cost to list of billing items
        items = items.concat([{
            color: Appearance.colors.primary(),
            key: 'total',
            title: costs?.post_trial_total ? 'Due Today' : 'Total',
            value: Utils.toCurrency(costs?.total ?? 0)
        }]);

        return items.filter(item => item.visible !== false);
    }

    const getPromotions = () => {
        return (
            <PromotionsManager 
            isPartnershipRequest={isPartnershipRequest}
            onChange={onPromotionsChange}
            promotions={promotions}
            utils={utils} />
        )
    }

    const fetchDynamicCosts = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { costs } = await Request.get(utils, '/subscriptions/', {
                id: tier.id,
                is_partnership_request: isPartnershipRequest,
                promotions: promotionsRef.current.map(promotion => promotion.code),
                schedule: schedule,
                type: 'get_dynamic_tier_costs'
            });

            // end loading and update local state with dynamic costs
            setLoading(false);
            setCosts(costs);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your customized subscription cost. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_subscription.fetch_dynamic_costs'
            });
        }
    }

    const fetchPaymentRequest = async () => {
        try {
        
            // send request to server to validate payment request
            setLoading('payment_request');
            let { ala_carte, payment_method_id, promotions, schedule, tier } = await Request.get(utils, '/payments/', {
                code: paymentRequest,
                type: 'validate_payment_request'
            });

            // update local state with request data
            //setAlaCarte(ala_carte);
            setPromotions(promotions);
            setSchedule(schedule);
            setTier(tier);

            // set default payment method using the provided type
            getSpecialtyPaymentMethods().forEach(method => {
                if(method.id === payment_method_id) {
                    setPaymentMethod(Payment.Method.create(method));
                    return;
                }
            });

            // end loading after a brief delay so states can update
            setTimeout(setLoading.bind(this, false), 1000);

        } catch(e) {
            setLayerState('close');
            utils.alert.show({
                message: `There was an issue processing the payment request. ${e.message || 'An unknown error occurred'}`,
                title: 'Oops!'
            });
        }
    }

    const includesDefaultPromotions = () => {
        return freeTrial === true || familyPlan === true || scheduleHasPremiumSetupDefault() === true || request.focus === 'third_party_referral' ? true : false;
    }

    const isStarterPlan = () => {
        return tier?.metadata.is_starter_plan === true;
    }

    const newDigitalWalletPayment = callback => {
        try {

            setLoading('submit');
            let { capabilities, request } = stripeIntent;
            if(!capabilities || !request) {
                throw new Error('Payment request is not available');
            }

            // verify that the requested digital wallet is available on the user's device before continuing
            switch(paymentMethod.id) {
                case 'apple_pay':
                if(capabilities.applePay === false) {
                    callback({ error: new Error('Apple Pay is not available on this device') });
                    return;
                }
                break;

                case 'google_pay':
                if(capabilities.googlePay === false) {
                    callback({ error: new Error('Google Pay is not available on this device') });
                    return;
                }
                break;
            }

            // register an event listener for token changes
            request.on('token', async evt => {
                evt.complete('success');
                callback({ 
                    payment_method_token: evt.token.id,
                    payment_method_type: 'digital_wallet' 
                });
            });

            // register event listener for payment request cancellation by user
            request.on('cancel', () => {
                callback({ error: new Error('user_cancelled') });
            });

            // present the payment sheet immediately (must be synchronous with user gesture)
            request.show();
            
        } catch(e) {
            callback({ error: e });
        }
    }

    const processSubscriptionUpgradeConfirmation = callback => {
        utils.alert.show({
            title: 'Subscription Upgrade',
            message: `You already have an active subscription. To proceed with activating the ${tier.title} plan, we'll need to send you a verification code to confirm this change. Do you wish to continue?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(callback.bind(this, { override_confirmation: true }), 500);
                    return;
                }
            }
        });
    }

    const processTwoFactorAuthenticationRequest = (category, callback) => {
        return new Promise((resolve, reject) => {

            // determine which type of content should be generated
            let content = {};
            switch(category) {
                case 'subscription_cancellation':
                content = {
                    message: `We've sent a verification code to the ${API.use_email_2fa ? 'email address' : 'phone number'} on file with your account. Please enter it to confirm the cancellation of your current subscription before we can process your new one..`,
                    title: 'Verification Required'
                }
                break;

                default: 
                content = {
                    message: `We've sent a verification code to the ${API.use_email_2fa ? 'email address' : 'phone number'} on file with your account. You'll need to enter it to confirm your identity before we can apply any changes to your account.`,
                    title: 'Verification Required'
                }
            }

            // show alert requesting confirmation from user
            let code = null;
            utils.alert.show({
                ...content,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => code = text}
                        placeholder={'Verification Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'continue',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(code && key === 'continue') {
                        setTimeout(callback.bind(this, { 
                            override_confirmation: true,
                            two_factor_verification_code: code 
                        }), 500);
                        resolve();
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const scheduleHasPremiumSetupDefault = () => {
        let schedules = Subscription.schedules.get();
        return [schedules.three_year, schedules.five_year, schedules.seven_year, schedules.ten_year].includes(defaultSchedule);
    }

    const setupDefaultPromotions = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { promotions } = await Request.get(utils, '/subscriptions/', {
                free_trial: freeTrial,
                family_plan: familyPlan,
                premium_setup: scheduleHasPremiumSetupDefault(),
                third_party_referral_provider: request.focus === 'third_party_referral' ? request.provider : null,
                tier_id: tier.id,
                type: 'new_subscription_resources'
            });

            // end loading and set promotions state
            setLoading(false);
            setPromotions(promotions);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving available promotions. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_subscription.setup_default_promotions'
            });
        }
    }

    const showFreeTrialDisclaimer = () => {
        return new Promise((resolve, reject) => {

            // log that disclaimer has been shown
            utils.analytics.log('subscription_free_trial_disclaimer_shown', {
                tier_id: tier.id,
                tier_name: tier.title
            });
            
            // show disclaimer to user with an alert
            utils.alert.show({
                title: '7 Day Free Trial',
                message: `The LifeWipe trial gives you full access to all features and benefits of your selected plan. However, emergency wipes and instruction executions are disabled during the trial period. Your payment method will be charged ${Utils.toCurrency(costs?.post_trial_total)} at the end of the trial period unless you cancel beforehand.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        resolve();
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const shouldShowStarterPlan = () => {
        if(request.mode === 'sales' || isActivationMode === true) {
            return false;
        }
        return showStarterPlan;
    }

    useEffect(() => {

        // determine if financing option should be enabled based on total cost
        let enabled = costs?.total > 0 ? true : false;
        setFinancingEnabled(enabled);

        // reset payment method selection if financing is not enabled and financing option is currently selected
        if(enabled === false && paymentMethod?.type === 'financing') {
            setPaymentMethod(null);
        }

    }, [costs]);

    useEffect(() => {
        onUpdateStripeIntent();
    }, [costs, paymentMethod]);

    useEffect(() => {
        paymentRequest && fetchPaymentRequest();
    }, [paymentRequest]);

    useEffect(() => {
        promotionsRef.current = promotions;
        if(tier) {
            if(includesDefaultPromotions() === true) {
                promotions.length > 0 && fetchDynamicCosts();
            } else {
                fetchDynamicCosts();
            }
            
        }
    }, [promotions, tier]);

    useEffect(() => {
        if(tier) {
            if(includesDefaultPromotions() === true) {
                setupDefaultPromotions();
            } else if(isStarterPlan() === false) {
                fetchDynamicCosts();
            }
        }
    }, [tier]);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            apiRequest: getApiRequestProps(),
            layerState: layerState,
            ...getLayerOptions()
        }}>
            {getContent()}
        </Layer>
    )
}

export const SalesOpsOrderForm = ({ schedule, tier },{ index, options, utils }) => {

    const layerID = 'sales_ops_order_form';
    const [costs, setCosts] = useState(null);
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [promotions, setPromotions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [tiers, setTiers] = useState([]);
    const { user, setRequiem } = useDataStore();

    const onAddPaymentMethodClick = () => {
        utils.layer.open({
            Component: AddPaymentMethod.bind(this, { 
                onChange: setPaymentMethod,
                onCreateToken: token => {
                    setPaymentMethod(token.card);
                    onUpdateTarget({
                        payment_method_id: 'credit_debit_card',
                        payment_method_type: 'card',
                        payment_method_token: token.id
                    });
                }
            }),
            id: 'add_payment_method'
        });
    }

    const onAskRequiemClick = () => {
        setRequiem(requiem => ({ 
            ...requiem,
            request: {
                action: 'open'
            }
        }));
    }

    const onBenefitDescriptionClick = benefit => {

        // log analytics event
        utils.analytics.log('view_benefit_description', { benefit_key: benefit.key });

        // show alert with benefit description
        utils.alert.show({
            title: benefit.title,
            message: benefit.description
        });
    }

    const onPaymentMethodTypeChange = item => {

        // reset payment method anytime the type changes
        setPaymentMethod(null);

        // reset edits for payment_method_type and should_send_payment_url fields
        if(!item) {
            onUpdateTarget({
                payment_method_id: null,
                payment_method_type: null,
                should_send_payment_url: false
            });
            return;
        }

        // find a speciality payment method match for the selection
        let method = getSpecialtyPaymentMethods().find(m => m.id === item.id);

        // update edits with new payment method type and whether a payment link should be sent based on the new type
        onUpdateTarget({ 
            payment_method_id: item.id,
            payment_method_type: method?.type || 'card',
            should_send_payment_url: paymentMethodRequiresPaymentLink(item.id) 
        });
    }

    const onPromotionsChange = targets => {
        setPromotions(targets);
    }

    const onSalesAssociateHeaderClick = () => {
        utils.alert.show({
            title: 'About Your Account',
            message: `You are logged in as ${user.full_name} and represent LifeWipe Inc in this transaction. If you need to switch accounts, please log out and log back in with the appropriate credentials before submitting this order.`,
            buttons: [{
                key: 'logout',
                title: 'Logout',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'logout') {
                    setLayerState('close');
                    setTimeout(utils.user.logout.bind(this, { remove_refresh_token: true }), 1000);
                    return;
                }
            }
        });
    }

    const onSendIdentityEmail = () => {
        return new Promise((resolve, reject) => {

            const onConfirm = async emailAddress => {
                try {

                    // submit request to server
                    await Request.post(utils, '/users/', {
                        email_address: emailAddress,
                        first_name: edits.first_name,
                        type: 'send_sales_ops_identity_email'
                    });

                    resolve({
                        banner: true,
                        message: 'Please allow  up to 5 minutes for the email to arrive in the customer’s inbox.',
                        title: 'Identity Email Sent'
                    });

                } catch(e) {
                    reject(e);
                }
            }

            let emailAddress = edits.email_address || selectedUser?.email_address;
            utils.alert.show({
                title: 'Identity Email',
                message: `This email will confirm your affiliation with LifeWipe Inc. Which email address should we send your identity verification to?`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => emailAddress = text}
                        placeholder={'Customer Email Address'} 
                        value={emailAddress} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Send',
                    style: 'default',
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm' && emailAddress) {
                        onConfirm(emailAddress);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onSubmitOrder = async () => {
        try {

            setLoading('submit');
            await validateRequiredFields(getFields);

            // verify that a payment method has been selected
            if(edits.payment_method_id === 'credit_debit_card' && !edits.payment_method_token && edits.should_send_payment_url === false) {
                throw new Error('Please select a payment method before submitting the order.');
            }

            // prevent moving forward if the required checkboxes have not been met
            if(!edits.purchase_amount || !edits.payment_authorization) {
                throw new Error(`Please confirm the customer understands the purchase amount, payment method, and LifeWipe's cancellation and refund policy, and that they authorize LifeWipe to process this payment today before submitting the order.`);
            }

            // submit request to server
            const { new_user, url } = await Request.post(utils, '/subscriptions/', {
                ...edits,
                promotions: promotions.map(p => p.id),
                type: 'new_sales_ops_subscription_request',
                user_id: selectedUser?.user_id
            });

            setLoading(false);

            // set new user object if a new user was created, this allows for resubmissions without re-creating the new user
            if(new_user) {
                onUpdateTarget({ user_type: 'existing' });
                setSelectedUser(new_user);
            }

            // determine if a payment link was generated
            if(url) {
                utils.alert.show({
                    title: 'Payment Link Sent',
                    message: `A payment link has been sent to the customer via ${edits.payment_url_delivery_method}. They will need to complete the payment to activate the subscription.`
                });
                return;
            }

            // show confirmation alert to sales associate
            utils.alert.show({
                title: 'Order Submitted',
                message: `Thank you. This completes your order confirmation. You will receive a written confirmation of your purchase and policy details. Welcome to LifeWipe.`
            });

        } catch(e) {

            setLoading(false);
            if(e.code === Request.status.get().confirmation_required) {
                utils.alert.show({
                    title: 'Subscription Conflict',
                    message: `This user already has an active subscription. They will need to cancel their existing subscription before starting a new one.`,
                });
                return;
            }
            utils.alert.error({
                error: e,
                message: `Unable to submit order. ${e.message || 'An unknown error occurred'}`,
                origin: 'sales_ops_order_form.on_submit_order'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits({ ...edits, ...props });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'cancel',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Cancel'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitOrder,
            text: 'Submit Order'
        }];
    }

    const getPaymentMethodTypeItems = () => {

        let methods = getSpecialtyPaymentMethods().map(method => {
            return {
                id: method.id,
                title: method.name
            }
        }).filter(item => {
            return scheduleSupportsPaymentMethodType(item.id, edits.schedule);
        });

        // add credit/debit card option to list of methods
        methods.push({ 
            id: 'credit_debit_card', 
            title: 'Credit/Debit Card'
        });

        // return alphabetically sorted methods
        return methods.sort((a,b) => {
            return a.title.localeCompare(b.title);
        });
    }

    const getPaymentUrlDeliveryMethodItems = () => {
        return [{ 
            id: 'email', 
            title: 'Email' 
        },{ 
            id: 'sms', 
            title: 'Text Message' 
        }];
    }

    const getScheduleItems = () => {
        let schedules = Subscription.schedules.get();
        return Object.values(schedules).filter(code => {
            return [schedules.daily, schedules.weekly, schedules.bi_weekly].includes(code) === false;
        }).map(code => ({
            id: code,
            title: Subscription.schedules.toText(code)
        }));
    }

    const getUserItems = () => {
        return [{ 
            id: 'new', 
            title: 'New Customer' 
        },{ 
            id: 'existing', 
            title: 'Existing Customer' 
        }];
    }

    const getDisclaimers = () => {

        // no disclaimers are needed until a tier and schedule have been selected
        if(!edits.tier_id || !edits.schedule) {
            return null;
        }

        // prepare selected tier
        let tier = tiers.find(tier => tier.id === edits.tier_id);

        // prepare items that the sales operative must read to the customer and confirm before submitting the order on their behalf
        let items = [{
            id: 'purchase_amount',
            title: `Do you understand and agree to the purchase amount, payment method, and LifeWipe’s cancellation and refund policy?`
        },{
            id: 'payment_authorization',
            title: `Do you authorize LifeWipe to process this payment today in the amount of ${Utils.toCurrency(costs?.total || 0)}?`
        }];

        // prepare list of non-refundable items for potential inclusion in disclaimer
        let nonRefundableItems = ['administrative fees'];
        promotions.forEach(promotion => {
            if(promotion.category === Subscription.promotions.categories.get().service) {
                nonRefundableItems.push(`the ${promotion.title.toLowerCase()} fee of ${Utils.toCurrency(promotion.amount)}`);
            }
        });
        nonRefundableItems.push('any operational costs already incurred');
        
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Confirmation & Disclosure'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        padding: '12px 16px 16px 16px',
                        whiteSpace: 'pre-wrap'
                    }}>{`Before we finish, I need to review and confirm your order for accuracy and compliance. You are purchasing LifeWipe ${tier?.title || 'Subscription'} Plan, which includes secure account setup, digital legacy management tools, and access to LifeWipe’s services as explained today. The total cost of your purchase is ${Utils.toCurrency(costs?.total || 0)}, which is a ${Subscription.schedules.toText(edits.schedule, false).toLowerCase()} payment. Your payment will be processed today using ${paymentMethod?.name || 'your desired payment method'}. \n\nLifeWipe offers a 7-day cancellation period starting today. If you choose to cancel within those 7 days, you will receive a refund minus ${Utils.oxfordImplode(nonRefundableItems)}. After 7 days but within 30 days of purchase, you are eligible for up to a 50% refund not including the aforementioned non-refundable costs.`}</span>
                    {items.map((item, index) => {
                        return (
                            <div
                            className={`view-entry ${window.theme}`}
                            key={index}
                            onClick={onUpdateTarget.bind(this, { [item.id]: edits[item.id] ? false : true })}
                            style={{
                                alignItems: 'center',
                                borderTop: Appearance.styles.bottomBorder(),
                                display: 'flex',
                                flexDirection: 'row',
                                padding: 16,
                                width: '100%'
                            }}>
                                <Checkbox 
                                checked={edits[item.id]}
                                onClick={val => onUpdateTarget({ [item.id]: val })} />
                                <span 
                                className={'cursor-pointer'}
                                style={{
                                    ...Appearance.textStyles.defaultText(),
                                    marginLeft: 12
                                }}>{item.title}</span>
                            </div>
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getFields = () => {

        // prepare list items
        let paymentMethodTypeItems = getPaymentMethodTypeItems();
        let paymentUrlDeliveryMethodItems = getPaymentUrlDeliveryMethodItems();
        let scheduleItems = getScheduleItems();
        let userItems = getUserItems();

        // prepare sections for mapper
        const fields = [{
            description: 'Identify the customer you’re working with. Search for an existing LifeWipe account or create a new one by entering their details below. This ensures the plan is attached to the correct profile and avoids duplicate accounts.',
            id: 'customer',
            title: 'Customer',
            items: [{
                component: 'list',
                id: 'user_id',
                items: userItems,
                onChange: item => {
                    onUpdateTarget({ user_type: item?.id });
                    setSelectedUser(null);
                },
                title: 'Type',
                value: userItems.find(item => item.id === edits.user_type)?.title
            },{
                component: 'user_lookup',
                id: 'user_id',
                onChange: setSelectedUser,
                placeholder: 'Search by name or email address',
                title: 'Lookup Customer',
                value: selectedUser,
                visible: edits.user_type === 'existing' ? true : false
            }]
        }];

        // add fields to input user details if applicable
        if(edits.user_type === 'new') {
            fields[0].items = fields[0].items.concat([{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'Phone Number',
                value: edits.phone_number
            }]);
        }

        // add section for plan selection
        fields.push({
            id: 'plan_selection',
            description: 'Choose the plan and billing schedule that best fits the customer’s needs. You can review tiers together and select monthly, annual, or multi-year billing before continuing.',
            title: 'Subscription',
            items: [{
                component: 'list',
                id: 'tier',
                items: tiers,
                onChange: item => {
                    let tier = tiers.find(tier => tier.id === item.id);
                    if(!tier) {
                        onUpdateTarget({
                            ala_carte: {},
                            tier_id: null
                        });
                        return;
                    }
                    onUpdateTarget({ 
                        ala_carte: {
                            devices: tier.preferences.benefits.devices.limit || 0,
                            locket_passwords: tier.preferences.benefits.locket.limit || 100000,
                            locket_accounts: tier.preferences.benefits.accounts.limit || 0,
                            trusted_contacts: tier.preferences.benefits.trusted_contacts.limit || 0
                        },
                        tier_id: item.id
                    });
                },
                title: 'Plan',
                value: tiers.find(tier => tier.id === edits.tier_id)?.title
            },{
                component: 'list',
                id: 'schedule',
                items: scheduleItems,
                onChange: item => {
                    onUpdateTarget({ schedule: item?.id });
                    if(paymentMethod && item) {
                        setPaymentMethod(scheduleSupportsPaymentMethodType(paymentMethod.id, item.id) ? paymentMethod : null);
                    }
                },
                title: 'Billing Schedule',
                value: scheduleItems.find(item => item.id === edits.schedule)?.title
            },{
                component: 'custom',
                required: false,
                title: 'Plan Benefits',
                value: edits.tier_id && getSubscriptionBenefitsOverview(),
                visible: edits.tier_id && edits.schedule ? true : false
            }]
        });

        /*
        // prepare fields for ala-carte additions
        if(edits.tier_id && edits.schedule) {
            let tier = tiers.find(tier => tier.id === edits.tier_id);
            fields.push({
                description: 'Customize plan limits as needed. Additional accounts, devices, trusted contacts, or locket passwords can be added à la carte for an adjusted price.',
                id: 'ala_carte',
                title: `${tier.title} Plan Services`,
                items: [{
                    component: 'textfield',
                    id: 'trusted_contacts',
                    onChange: val => {
                        onUpdateTarget({
                            ala_carte: update(edits.ala_carte || {}, {
                                trusted_contacts: {
                                    $set: val
                                }
                            })
                        });
                    },
                    props: {
                        format: 'number'
                    },
                    title: 'Trusted Contacts',
                    value: edits.ala_carte?.trusted_contacts || 0
                },{
                    component: 'textfield',
                    id: 'devices',
                    onChange: val => {
                        onUpdateTarget({
                            ala_carte: update(edits.ala_carte || {}, {
                                devices: {
                                    $set: val
                                }
                            })
                        });
                    },
                    props: {
                        format: 'number'
                    },
                    title: 'Devices',
                    value: edits.ala_carte?.devices || 0
                },{
                    component: 'textfield',
                    id: 'locket_passwords',
                    onChange: val => {
                        onUpdateTarget({
                            ala_carte: update(edits.ala_carte || {}, {
                                locket_passwords: {
                                    $set: val
                                }
                            })
                        });
                    },
                    props: {
                        format: 'number'
                    },
                    title: 'Locket Passwords',
                    value: edits.ala_carte?.locket_passwords || 0
                },{
                    component: 'textfield',
                    id: 'locket_accounts',
                    onChange: val => {
                        onUpdateTarget({
                            ala_carte: update(edits.ala_carte || {}, {
                                locket_accounts: {
                                    $set: val
                                }
                            })
                        });
                    },
                    props: {
                        format: 'number'
                    },
                    title: 'Secured Locket Accounts',
                    value: edits.ala_carte?.locket_accounts || 0
                }]
            });
        }
        */

        // present option for payment method type selection
        fields.push({
            description: 'Select how the customer would like to complete payment. If a digital wallet or financing option is chosen, a secure payment link will be emailed to them. Card payments can be completed directly during this step.',
            id: 'payment',
            lastItem: false,
            title: 'Payment',
            items: [{
                component: 'list',
                id: 'payment_method_id',
                items: paymentMethodTypeItems,
                onChange: onPaymentMethodTypeChange,
                title: 'Payment Method',
                value: paymentMethodTypeItems.find(method => method.id === edits.payment_method_id)?.title
            },{
                component: 'bool_list',
                id: 'should_send_payment_url',
                onChange: val => {
                    setPaymentMethod(null);
                    onUpdateTarget({ 
                        should_send_payment_url: val,
                        payment_url_delivery_method: null
                    });
                },
                title: 'Send Secure Payment Link',
                value: edits.should_send_payment_url || false,
                visible: edits.payment_method_id === 'credit_debit_card'
            },{
                component: 'textfield',
                id: 'card_details',
                onClick: onAddPaymentMethodClick, 
                props: {
                    placeholder: 'Click to add a credit or debit card...'
                },
                title: 'Card Information',
                value: paymentMethod ? `${Utils.ucFirst(paymentMethod.brand)} ${Utils.ucFirst(paymentMethod.funding)} (${paymentMethod.last4})` : null,
                visible: edits.payment_method_id === 'credit_debit_card' && edits.should_send_payment_url === false ? true : false
            },{
                component: 'list',
                id: 'payment_url_delivery_method',
                items: paymentUrlDeliveryMethodItems,
                onChange: item => onUpdateTarget({ payment_url_delivery_method: item?.id }),
                title: 'Payment Link Delivery Method',
                value: paymentUrlDeliveryMethodItems.find(method => method.id === edits.payment_url_delivery_method)?.title,
                visible: edits.should_send_payment_url === true
            }]
        });

        return fields;
    }

    const getNotes = () => {
        return (
            <LayerItem 
            collapsed={false}
            title={'Notes'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px 16px 16px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        marginBottom: 16
                    }}>{`Add any internal notes related to this sale — including customer objections, unique requests, payment considerations, or details worth flagging for future reference.`}</span>
                    <TextView 
                    placeholder={'Enter any notes related to this sale here...'}
                    onChange={text => onUpdateTarget({ notes: text })}
                    value={edits.notes}
                    style={{
                        minHeight: 125,
                        width: '100%'
                    }} />
                </div>
            </LayerItem>
        )
    }

    const getOrderOverview = () => {
        
        // no additional logic is required if costs have not been calculated
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 12,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }

        return getOrderOverviewItems().map((item, index, items) => {
            return (
                <div 
                key={index}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== items.length - 1 && Appearance.styles.bottomBorder(),
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: 12,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                    }}>{item.title}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                        ...item.color && {
                            color: item.color
                        }
                    }}>{item.value}</span>
                </div>
            )
        })
    }

    const getOrderOverviewItems = () => {

        // prepare billing items with dynamic costs
        let items = [{
            key: 'billing',
            title: 'Billing Cycle',
            value: Subscription.schedules.toText(edits.schedule)
        },{
            key: 'payment_method',
            title: 'Payment Method',
            value: paymentMethod?.name || 'Not Selected',
            visible: paymentMethod ? true : false
        },{
            key: 'billing_start_date',
            title: 'First Payment Date',
            value: Utils.format.date(costs?.billing_start_date, { exclude_time: true}),
            visible: costs?.billing_start_date ? true : false
        },{
            key: 'renewal_date',
            title: 'Renewal Date',
            value: Utils.format.date(costs?.renewal_date, { exclude_time: true})
        },{
            key: 'base_cost',
            title: 'Subscription Cost',
            value: Utils.toCurrency(costs?.base ?? 0)
        }];

        // determine if at least one promotion is present
        if(costs?.promotions?.length > 0) {

            // add available promotions for service prior to sub-total
            costs.promotions.forEach(promotion => {
                if(promotion.category === Subscription.promotions.categories.get().service) {
                    items.push({
                        key: promotion.id,
                        title: promotion.title,
                        value: Utils.toCurrency(promotion.amount)
                    });
                }
            });

            // add sub-total line items 
            items.push({
                key: 'sub_total',
                title: 'Sub-Total',
                value: Utils.toCurrency(costs?.sub_total ?? 0)
            });

            // add available promotions for discount after sub-total
            costs.promotions.forEach(promotion => {
                if(promotion.category !== Subscription.promotions.categories.get().service) {
                    items.push({
                        color: Appearance.colors.gold,
                        key: promotion.id,
                        title: `${promotion.title} Promotion`,
                        value: `-${Utils.toCurrency(promotion.amount)}`
                    });
                }
            });
        }

        // add total cost to list of billing items
        items = items.concat([{
            color: Appearance.colors.primary(),
            key: 'total',
            title: costs?.post_trial_total ? 'Due Today' : 'Total',
            value: Utils.toCurrency(costs?.total ?? 0)
        }]);

        return items.filter(item => item.visible !== false);
    }

    const getOrderOverviewLayerItem = () => {
        if(!edits.tier_id || !edits.schedule) {
            return null;
        }
        return (
            <LayerItem 
            collapsed={false}
            lastItem={false}
            title={'Plan Overview'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {getOrderOverview()}
                </div>
            </LayerItem>
        )
    }

    const getPromotions = () => {
        return (
            <PromotionsManager
            description={'Apply any applicable discounts, setup-costs, or promotional codes. This step is optional and only used when a promotion has been offered or approved.'}
            onChange={onPromotionsChange}
            promotions={promotions}
            utils={utils} />
        )
    }

    const getSalesAssociateHeader = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={false}
            title={'Sales Order Form'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem
                    description={`You're logged in as ${user.username}, and any orders you complete will be securely attributed to your account.`}
                    image={user.avatar}
                    onClick={onSalesAssociateHeaderClick}
                    title={`Welcome back, ${user.first_name}.`} 
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'Provide the customer with a proof of identity email for your involvement with LifeWipe Inc.'}
                    image={'images/sales-ops-send-identity-email.png'}
                    onAsyncClick={onSendIdentityEmail}
                    title={'Send Identity Email'}
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'Questions about a specific feature, workflow, or customization? Ask Requiem for assistance.'}
                    image={'images/requiem-ai-green.png'}
                    lastItem={true}
                    onClick={onAskRequiemClick}
                    title={'Learn More with Requiem'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getSubscriptionBenefitsOverview = () => {

        // prepare applicable benefits for selected tier
        let tier = tiers.find(tier => tier.id === edits.tier_id);
        let targets = tier.benefits.filter(benefit => benefit.value === null || benefit.value === false ? false : true);

        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                borderRadius: 8,
                width: '100%'
            }}>
                {targets.map((benefit, index, benefits) => {
                    return (
                        <div 
                        className={`view-entry ${window.theme}`}
                        onClick={onBenefitDescriptionClick.bind(this, benefit)}
                        key={index}
                        style={{
                            alignItems: 'center',
                            borderBottom: index !== benefits.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            padding: 12,
                            textAlign: 'left',
                            width: '100%'
                        }}>
                            <img 
                            src={window.theme === 'dark' ? 'images/asterick-badge-dark.png' : 'images/asterick-badge-light.png'}
                            style={{
                                height: 25,
                                marginRight: 8,
                                objectFit: 'contain',
                                width: 25 
                            }} />
                            <span style={{
                                ...Appearance.textStyles.defaultTextSmall(),
                                flexGrow: 1
                            }}>
                                {`${benefit.title}: `}
                                <span style={{
                                    ...['Priority','Unlimited','Premium'].includes(benefit.value) && {
                                        color: Appearance.colors.primary(),
                                        fontWeight:  'bold' 
                                    }
                                }}>{benefit.value}</span>
                            </span>
                            <img
                            src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                            style={{
                                height: 12,
                                objectFit: 'contain',
                                opacity: 0.25,
                                width: 12
                            }} />
                        </div>
                    )
                })}
            </div>
        );
    }

    const fetchDynamicCosts = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { costs } = await Request.get(utils, '/subscriptions/', {
                ala_carte: {
                    accounts: edits.accounts,
                    devices: edits.devices,
                    locket_passwords: edits.locket_passwords,
                    trusted_contacts: edits.trusted_contacts
                },
                id: edits.tier_id,
                promotions: promotions.map(promotion => promotion.code),
                schedule: edits.schedule,
                type: 'get_dynamic_tier_costs'
            });

            // end loading and update local state with dynamic costs
            setLoading(false);
            setCosts(costs);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the customized subscription cost. ${e.message || 'An unknown error occurred'}`,
                origin: 'sales_ops_order_form.fetch_dynamic_costs'
            });
        }
    }

    const fetchResources = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { promotions, tiers } = await Request.get(utils, '/subscriptions/', {
                type: 'sales_ops_order_form_resources'
            });

            // end loading and update local state
            setTiers(tiers);
            setPromotions(promotions);
            setLoading(false);
 
            // setup default edits objects
            setEdits({
                ala_carte: {},
                schedule: schedule,
                ...tier && {
                    ala_carte: {
                        devices: tier.preferences.benefits.devices.limit || 0,
                        locket_passwords: tier.preferences.benefits.locket.limit || 100000,
                        locket_accounts: tier.preferences.benefits.accounts.limit || 0,
                        trusted_contacts: tier.preferences.benefits.trusted_contacts.limit || 0
                    },
                    tier_id: tier.id
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `Unable to fetch subscription tiers. ${e.message}`,
                origin: 'sales_ops_order_form.fetch_tiers'
            });
        }
    }

    const paymentMethodRequiresPaymentLink = method => {

        // any method other than a credit or debit card requires a payment link
        return method ? method !== 'credit_debit_card' : false;
    }

    const scheduleSupportsPaymentMethodType = (type, schedule) => {

        // no further logic is required if financing wasnt requested
        if(type !== 'klarna') {
            return true;
        }

        // compare schedule against supported schedules
        let schedules = Subscription.schedules.get();
        return [schedules.annually, schedules.three_year, schedules.five_year, schedules.seven_year, schedules.ten_year].includes(schedule) === true
    }

    useEffect(() => {
        if(edits.tier_id && edits.schedule) {
            fetchDynamicCosts();
        }
    }, [edits.accounts, edits.devices, edits.tier_id, edits.locket_passwords, edits.trusted_contacts, edits.schedule, promotions]);

    useEffect(() => {
        fetchResources();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}
        utils={utils}>
            {getSalesAssociateHeader()}
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            {getPromotions()}
            {getNotes()}
            {getOrderOverviewLayerItem()}
            {getDisclaimers()}
        </Layer>
    )
}

export const Payments = ({ utils }) => {

    const limit = 10;
    const offset = useRef(0);
    const paymentHistory = useRef(null);

    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [payments, setPayments] = useState([]);
    const { requiem, setRequiem } = useDataStore();

    const onPaymentClick = payment => {
        utils.layer.open({
            abstract: Abstract.create({
                object: payment,
                type: 'payments'
            }),
            Component: PaymentDetails,
            id: `payment_details_${payment.id}`
        });
    }

    const onProcessRequiemNavigationRequest = () => {

        // no further action is needed if there is no target match
        if(requiem.action?.target !== 'billing') {
            return;
        }

        // determine next steps for the action request
        switch(requiem.action?.key) {
            case 'payment_history':
            setTimeout(() => {
                window.scrollTo({
                    behavior: "smooth",
                    left: 0,
                    top: paymentHistory.current?.offsetTop
                });
            }, 1500);
            break;   
        }

        // reset requiem object for next request
        setRequiem({ ...requiem, action: null });
    }

    const getPayments = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(payments.length === 0) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payments-icon-dark.png' : 'images/payments-icon-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{('No Payments Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            whiteSpace: 'normal'
                        }}>{'We were unable to locate any payments for your account.'}</span>
                    </div>
                </div>
            )
        }
        return payments.map((payment, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onPaymentClick.bind(this, payment)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== payments.length - 1 && Appearance.styles.bottomBorder(),
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payments-icon-dark.png' : 'images/payments-icon-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        maxWidth: '100%',
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{(payment.title || 'General Payment').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText({ color: Appearance.colors.subText() })
                        }}>{Utils.format.date(payment.date, { exclude_time: true })}</span>
                    </div>
                    <AltBadge 
                    className={'d-none d-xl-flex'}
                    content={{
                        color: Appearance.colors.primary(),
                        text: Utils.toCurrency(payment.amount)
                    }} 
                    style={{
                        marginRight: 0 
                    }} />
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 20,
                        marginLeft: 12,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const fetchPayments = async () => {
        try {
            setLoading(true);
            let { payments, paging } = await Request.get(utils, '/payments/', {
                limit: limit,
                offset: offset.current,
                type: 'list'
            });

            setLoading(false);
            setPayments(payments.map(payment => Payment.create(payment)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your list of payments. ${e.message || 'An unknown error occurred'}`,
                origin: 'payments.fetch_payments'
            });
        }
    }

    useEffect(() => {
        onProcessRequiemNavigationRequest();
    }, [requiem]);

    useEffect(() => {
        fetchPayments();
        utils.content.subscribe('payments_list', ['payments','payment_methods'], {
            onFetch: type => {
                type === 'payments' && fetchPayments();
            },
            onUpdate: abstract => {
                switch(abstract.type) {
                    case 'payments':
                    setPayments(payments => {
                        return payments.map(payment => {
                            return payment.id === abstract.getID() ? abstract.object : payment;
                        });
                    });
                    break;

                    case 'payment_methods':
                    setPayments(payments => {
                        return payments.map(payment => {
                            if(payment.payment_method?.id === abstract.getID()) {
                                payment.payment_method = abstract.object;
                            }
                            return payment;
                        });
                    });
                    break;
                }
            }
        });
        return () => {
            utils.content.unsubscribe('payments_list');
        }
    }, []);

    return (
        <div 
        ref={paymentHistory}
        style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer 
                body={`Below you'll find a detailed record of all past payments and invoices associated with your account. You can view payment dates, amounts, invoice numbers, and statuses to easily track your transaction history.`}
                title={'Payment History'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {getPayments()}
                    </div>
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchPayments();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const PaymentDetails = ({ abstract, index, options, utils }) => {

    const layerID = `payment_details_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { user } = useDataStore();

    const onResendInvoiceClick = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async emailAddress => {
                try {
        
                    // submit request to server
                    await Request.post(utils, '/payments/', {
                        email_address: emailAddress,
                        id: abstract.getID(),
                        type: 'resend_invoice'
                    });

                    // end loading and notify origin that promise has been resolved
                    resolve({ 
                        banner: true,
                        message: `We have send a copy of your invoice to ${emailAddress}. Please allow up to 5 minutes for the message to be delivered.`,
                        title: 'Invoice Resent'
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue resending the invoice for this payment. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let emailAddress = user.email_address;
            utils.alert.show({
                title: 'Resend Invoice',
                message: `Which email address should we use when sending your invoice?`,
                content: (
                    <div style={{
                        paddingBottom: 24,
                        paddingLeft: 24,
                        paddingRight: 24,
                        width: '100%'
                    }}>
                        <TextField 
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => emailAddress = text}
                        placeholder={'Email Address'}
                        value={emailAddress} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Send',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(emailAddress && key === 'confirm') {
                        setTimeout(onConfirm.bind(this, emailAddress), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const getBillingLineItems = () => {

        let { line_items = {} } = abstract.object.metadata || {};

        // prepare default list of line items
        let items = [];

        // add payment method to line items if available
        if(abstract.object.payment_method) {
            items.push({
                key: 'payment_method',
                title: 'Payment Method',
                value: abstract.object.payment_method?.name || 'Unavailable'
            });
        }

        // add external payment provider if applicable
        if(abstract.object.metadata.external_payment_provider) {
            items.push({
                key: 'external_payment_provider',
                title: 'Payment Processing',
                value: Utils.ucFirst(abstract.object.metadata.external_payment_provider)
            });
        }   

        // process subscription payment line items
        if(abstract.object.category === Payment.categories.get().subscription_premium) {

            // prepare billing items with dynamic costs
            items = items.concat([{
                key: 'base_cost',
                title: 'Base Cost',
                value: Utils.toCurrency(line_items.base ?? 0)
            }]);

            // determine if at least one promotion is present
            if(line_items.promotions?.length > 0) {

                // add available promotions for service prior to sub-total
                line_items.promotions.forEach(promotion => {
                    if(promotion.category === Subscription.promotions.categories.get().service) {
                        items.push({
                            key: promotion.id,
                            title: promotion.title,
                            value: Utils.toCurrency(promotion.amount)
                        });
                    }
                });

                // add sub-total line items 
                items.push({
                    key: 'sub_total',
                    title: 'Sub-Total',
                    value: Utils.toCurrency(line_items.sub_total ?? 0)
                });

                // add available promotions for discount after sub-total
                line_items.promotions.forEach(promotion => {
                    if(promotion.category !== Subscription.promotions.categories.get().service) {
                        items.push({
                            color: Appearance.colors.gold,
                            key: promotion.id,
                            title: `${promotion.title} Promotion`,
                            value: `-${Utils.toCurrency(promotion.amount)}`
                        });
                    }
                });
            }

            // add total cost to list of billing items
            return items.concat([{
                color: Appearance.colors.primary(),
                key: 'total',
                title: 'Total Cost',
                value: Utils.toCurrency(line_items.total ?? 0)
            }]);
        }

        // process ala-carte add-on line items
        if(abstract.object.category === Payment.categories.get().ala_carte_accounts) {
            items = items.concat([{
                key: 'subscription_tier_limit',
                title: `Included ${line_items.tier_title} Tier Accounts`,
                value: line_items.subscription_tier_limit
            },{
                key: 'paid_accounts',
                title: 'Newly Purchased Accounts',
                value: Utils.softNumberFormat(line_items.paid_accounts)
            },{
                key: 'cost_per_account',
                title: 'Cost Per Account',
                value: Utils.toCurrency(line_items.cost_per_account)
            },{
                key: 'base_cost',
                title: 'Base Cost',
                value: Utils.toCurrency(line_items.cost_per_account * line_items.paid_accounts)
            },{
                color: Appearance.colors.yellow,
                key: 'pro_rate_percentage',
                title: 'Pro Rated Discount',
                value: `-${Utils.toCurrency((line_items.cost_per_account * line_items.paid_accounts) - abstract.object.amount)}`
            },{
                color: Appearance.colors.primary(),
                key: 'total',
                title: 'Total Cost',
                value: Utils.toCurrency(abstract.object.amount)
            }]);
        }
        
        return items;
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            key: 'details',
            title: 'Details',
            items: [{
                key: 'description',
                title: 'Description',
                value: abstract.object.title
            },{
                key: 'id',
                title: 'ID',
                value: `PAY-${abstract.object.id}`
            },{
                key: 'date',
                title: 'Processing Date',
                value: Utils.format.date(abstract.object.date)
            }]
        },{
            collapsed: false,
            key: 'costs',
            lastItem: false,
            title: 'Costs and Billing',
            items: getBillingLineItems()
        }];
    }

    const getFinancingSupportOptions = () => {
        let { financing_provider } = abstract.object.metadata || {};
        if(financing_provider === 'klarna') {
            return (
                <AsyncInteractionItem 
                description={'If you have questions about your Klarna financing, please contact Klarna support directly for assistance.'}
                image={'images/payment-details-financing-klarna.png'}
                onClick={() => window.open('https://www.klarna.com/us/customer-service/')} 
                title={'Financing Support'}
                utils={utils} />
            )
        }
    }

    const getOptionComponents = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Options'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {getFinancingSupportOptions()}
                    <AsyncInteractionItem 
                    description={'This will send a copy of the payment invoice to the email address of your choosing.'}
                    image={'images/send-icon-small.png'}
                    lastItem={true}
                    onAsyncClick={onResendInvoiceClick}
                    title={'Resend Invoice'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}>
            <FieldMapper
            sections={getFields()}
            utils={utils} />
            {getOptionComponents()}
        </Layer>
    )
}

export const PaymentMethods = ({ utils }) => {

    const limit = 10;
    const offset = useRef(0);

    const [loading, setLoading] = useState(false);
    const [methods, setMethods] = useState([]);
    const [paging, setPaging] = useState(null);
    const { system } = useDataStore();

    const onAddNewMethodClick = () => {
        utils.layer.open({
            Component: AddPaymentMethod.bind(this, { 
                onChange: method => {

                    // add newly created method to list of available payment methods
                    setMethods(methods => {
                        return update(methods, {
                            $push: [method],
                            $apply: methods => methods.sort((a,b) => {
                                return a.name.localeCompare(b.name);
                            })
                        });
                    });
                }
            }),
            id: 'add_payment_method'
        });
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'add_new_method',
            onClick: onAddNewMethodClick,
            text: 'Add Payment Method'
        }];
    }

    const onPaymentMethodClick = method => {
        utils.layer.open({
            abstract: Abstract.create({
                object: method,
                type: 'payment_methods'
            }),
            Component: EditPaymentMethod,
            id: `edit_payment_method_${method.id}`
        });
    }

    const getPaymentMethods = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(methods.length === 0) {
            return (
                <div style={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.header(),
                        fontFamily: 'Zen Dots'
                    }}>{`No Payment Methods Found`.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        color: Appearance.colors.subText(),
                        whiteSpace: 'normal'
                    }}>{`We were unable to locate any payment methods for your account.`}</span>
                </div>
            )
        }
        return methods.map((method, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onPaymentMethodClick.bind(this, method)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== methods.length - 1 && Appearance.styles.bottomBorder(),
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-lg-flex'}
                    src={method.brand.image_url || (window.theme === 'dark' ? 'images/payment-method-dark.png' : 'images/payment-method-light.png')}
                    style={{
                        flex: 'none',
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0,
                        overflow: 'hidden'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }}>{method.name.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }}>{method.getExpiration()}</span>
                    </div>
                    {system.window.isMobile === false && method.is_default && (
                        <AltBadge 
                        content={{
                            color: Appearance.colors.primary(),
                            text: 'Default'
                        }} 
                        style={{
                            flex: 'none',
                            marginRight: 0 
                        }} />
                    )}
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        flex: 'none',
                        height: 20,
                        marginLeft: 12,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            let { payment_methods, paging } = await Request.get(utils, '/payments/', {
                limit: limit,
                offset: offset.current,
                type: 'list_payment_methods'
            });

            setLoading(false);
            setMethods(payment_methods.map(method => Payment.Method.create(method)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your payment methods list. ${e.message || 'An unknown error occurred'}`,
                origin: 'payment_methods.fetch_payment_methods'
            });
        }
    }

    useEffect(() => {
        fetchPaymentMethods();
        utils.content.subscribe('payment_methods_list', ['payment_methods'], {
            onFetch: fetchPaymentMethods,
            onUpdate: abstract => {
                setMethods(methods => {
                    return methods.map(method => {
                        return method.id === abstract.getID() ? abstract.object : method;
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe('payment_methods_list');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Below are your saved payment methods, including the card type, last four digits, and expiration date. Your default payment method is marked with a "Default" badge, and you can easily add, remove, or update any of your payment methods by clicking on them.`}
                buttons={getContainerButtons()}
                title={'Payment Methods'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {getPaymentMethods()}
                    </div>
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const PaymentMethodSelector = ({ defaultID, financingEnabled = false, isPartnershipRequest = false, onChange, schedule, utils, value }) => {

    const componentID = 'payment_method_selector';
    const [loading, setLoading] = useState(false);
    const [methods, setMethods] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState(value);

    const onAddNewMethodClick = () => {

        // remove previously selected method
        setSelectedMethod(null);

        // open layer to add new payment method
        utils.layer.open({
            Component: AddPaymentMethod.bind(this, { 
                isPartnershipRequest,
                onChange: method => {

                    // set newly created payment method as the selected method
                    setSelectedMethod(method);

                    // add newly created method to list of available payment methods
                    setMethods(methods => {
                        return update(methods, {
                            $push: [method],
                            $apply: methods => methods.sort((a,b) => {
                                return a.name.localeCompare(b.name);
                            })
                        });
                    });

                    // notify subscribers of data change
                    if(typeof(onChange) === 'function') {
                        onChange(method);
                    }
                }
            }),
            id: 'add_payment_method'
        });
    }

    const onSelectedMethodChange = method => {

        // update local state with newly selected method
        setSelectedMethod(method);

        // notify subscribers of data change
        if(typeof(onChange) === 'function') {
            onChange(method);
        }
    }

    const shouldShowFinancingOption = method => {
        if(financingEnabled === false) {
            return false;
        }
        const schedules = Subscription.schedules.get();
        return [schedules.annually, schedules.three_year, schedules.five_year, schedules.seven_year, schedules.ten_year].includes(schedule);
    }

    const getPaymentMethods = () => {
        if(methods === null || loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        return (
            <>
            {methods.map((method, index) => {
                if(method.type === 'financing' && shouldShowFinancingOption(method) === false) {
                    return null;
                }
                return (
                    <div
                    className={`view-entry ${window.theme}`}
                    key={index}
                    onClick={onSelectedMethodChange.bind(this, method)}
                    style={{
                        alignItems: 'center',
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        display: 'flex',
                        flexDirection: 'row',
                        padding: 10,
                        width: '100%',
                    }}>
                        <img 
                        src={method.brand.image_url || (window.theme === 'dark' ? 'images/payment-method-card-dark.png' : 'images/payment-method-card-light.png')}
                        style={{
                            height: 35,
                            marginRight: 8,
                            objectFit: 'contain',
                            width: 35
                        }} />
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1
                        }}>
                            <span style={{
                                ...Appearance.textStyles.subTitle(),
                                color: Appearance.colors.text()
                            }}>{method.name}</span>
                            <span style={{
                                ...Appearance.textStyles.subTitle()
                            }}>{method.getExpiration()}</span>
                        </div>
                        {selectedMethod?.id === method.id && (
                            <AltBadge 
                            content={{
                                color: Appearance.colors.green,
                                text: 'Selected'
                            }} 
                            style={{
                                marginRight: 0 
                            }} />
                        )}
                        {selectedMethod?.id !== method.id && (
                            <img
                            src={'images/next-arrow-grey-small.png'}
                            style={{
                                height: 12,
                                marginLeft: 8,
                                objectFit: 'contain',
                                opacity: 0.75,
                                width: 12
                            }} />
                        )}
                    </div>
                )
            })}
            <div 
            className={`view-entry ${window.theme}`}
            onClick={onAddNewMethodClick}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                padding: 10,
                width: '100%',
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: Appearance.colors.text(),
                    flexGrow: 1
                }}>{'Add New Payment Method'}</span>
                <img
                src={'images/next-arrow-grey-small.png'}
                style={{
                    height: 12,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 12
                }} />
            </div>
            </>
        )
    }

    const fetchPaymentMethods = async () => {
        try {

            // start loading and send request to server
            setLoading(true);
            let { payment_methods } = await Request.get(utils, '/payments/', {
                is_partnership_request: isPartnershipRequest,
                type: 'list_payment_methods'
            });

            // add specialy payment options to list of targets
            let targets = getSpecialtyPaymentMethods().map(method => Payment.Method.create(method));

            // format payment methods as local method class
            payment_methods.forEach(method => {
                targets.push(Payment.Method.create(method));
            });

            // end loading and update local state
            setLoading(false);
            setMethods(targets);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your list of payment methods. ${e.message || 'An unknown error occurred'}`,
                origin: 'payment_methods.fetch_payment_methods'
            });
        }
    }

    useEffect(() => {
        if(methods && !value) {

            // set default target 
            let target = methods.find(target => defaultID ? target.id === defaultID : target.type === 'card');
            setSelectedMethod(target);

            // notify subscribers if a target was set
            if(target && typeof(onChange) === 'function') {
                onChange(target);
            }
        }
    }, [defaultID, methods]);

    useEffect(() => {
        setSelectedMethod(value);
    }, [value]);

    useEffect(() => {
        fetchPaymentMethods();
        utils.content.subscribe(componentID.current, ['payment_methods'], {
            onFetch: fetchPaymentMethods,
            onUpdate: abstract => {
                setMethods(methods => {
                    return methods.map(method => {
                        return method.id === abstract.getID() ? abstract.object : method;
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe(componentID.current);
        }
    }, []);

    return getPaymentMethods()
}

export const PayoutDetails = ({ abstract, index, options, utils }) => {

    const layerID = `payouts.${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [payout, setPayout] = useState(abstract.object);
    const { sockets, user } = useDataStore();
    const [reversals, setReversals] = useState([]);

    const onPayoutStatusChange = evt => {
        try {
            console.log(evt);
            if(abstract.getID() === evt.id) {
                abstract.object.status = evt.status;
                setPayout(abstract.object);
            }

        } catch(e) {
            console.error(e.message);
        }
    }
    const onResendInvoiceClick = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async emailAddress => {
                try {
        
                    // submit request to server
                    await Request.post(utils, '/payments/', {
                        email_address: emailAddress,
                        id: abstract.getID(),
                        type: 'resend_payout_invoice'
                    });

                    // end loading and notify origin that promise has been resolved
                    resolve({ message: `We have send a copy of your invoice to ${emailAddress}. Please allow up to 5 minutes for the message to be delivered.` });
        
                } catch(e) {
                    let error = new Error(`There was an issue resending the invoice for this payout. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let emailAddress = user.email_address;
            utils.alert.show({
                title: 'Resend Invoice',
                message: `Which email address should we use when sending your invoice?`,
                content: (
                    <div style={{
                        paddingBottom: 24,
                        paddingLeft: 24,
                        paddingRight: 24,
                        width: '100%'
                    }}>
                        <TextField 
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => emailAddress = text}
                        placeholder={'Email Address'}
                        value={emailAddress} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Send',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(emailAddress && key === 'confirm') {
                        setTimeout(onConfirm.bind(this, emailAddress), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const getAdjustedAmount = () => {
        return reversals.reduce((total, reversal) => {
            return total - reversal.amount;
        }, payout.amount);
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getContent = () => {
        if(!payout) {
            return null;
        }
        return (
            <>
            {getOverview()}
            <FieldMapper
            sections={getFields()}
            utils={utils} />
            {getOptionComponents()}
            </>
        )
    }

    const getFields = () => {

        // prepare default sections
        let sections = [{
            collapsed: false,
            key: 'details',
            lastItem: false,
            title: 'Details',
            items: [{
                color: reversals.length === 0 && Appearance.colors.green,
                key: 'amount',
                title: 'Amount',
                value: Utils.toCurrency(payout.amount),
            },{
                color: Appearance.colors.yellow,
                key: 'current_amount',
                title: 'Adjusted Amount',
                value: Utils.toCurrency(getAdjustedAmount()),
                visible: reversals.length > 0 ? true : false
            },{
                key: 'date',
                title: 'Created',
                value: Utils.format.date(payout.date)
            },{
                key: 'description',
                title: 'Description',
                value: payout.description
            },{
                key: 'resource_id',
                title: 'ID',
                value: payout.resource_id
            },{
                key: 'transit_start_date',
                title: 'Scheduled Transit Date',
                value: Utils.format.date(payout.transit_start_date, { exclude_time: true })
            },{
                color: payout.status.color,
                key: 'status',
                title: 'Status',
                value: payout.status.text
            }]
        }];

        // determine if at least one reversal is available
        if(reversals.length > 0) {
            sections.push({
                collapsed: false,
                key: 'reversals',
                lastItem: false,
                title: 'Reversals',
                items: reversals.map(reversal => ({
                    color: Appearance.colors.red,
                    key: reversal.id,
                    title: Utils.format.date(reversal.date),
                    value: `-${Utils.toCurrency(reversal.amount)}`
                }))
            });
        }

        return sections;
    }

    const getOptionComponents = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Options'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'This will send a copy of the payment invoice to the email address of your choosing.'}
                    image={'images/send-icon-small.png'}
                    lastItem={true}
                    onAsyncClick={onResendInvoiceClick}
                    title={'Resend Invoice'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getOverview = () => {
        return (
            <LayerItem 
            collapsed={false}
            title={'Transit Schedules'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        padding: '12px 16px 16px 16px',
                    }}>{`To help reduce payment failures and ensure funds have cleared from the customer’s bank, we use a 2-day rolling payout schedule. This delay gives time for any potential disputes, refunds, or failed charges to surface before funds are in-transit, improving reliability and reducing reversals.`}</span>
                </div>
            </LayerItem>
        )
    }

    const fetchTransfers = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { transfers } = await Request.get(utils, '/payments/', {
                id: abstract.getID(),
                type: 'payout_transfers'
            });

            // end loading and show confirmation alert
            setLoading(false)
            setReversals(transfers.reduce((reversals, transfer) => {
                if(transfer.metadata.reversals?.length > 0) {
                    transfer.metadata.reversals.forEach(reversal => {
                        reversals.push({
                            ...reversal,
                            date: moment.utc(reversal.date).local()
                        })
                    })
                }
                return reversals;
            }, []));

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the transfers for this payout. ${e.message || 'An unknown error occurred'}`,
                origin: 'payout_details.fetch_transfers'
            });
        }
    }

    const connectToSockets = () => {
        sockets.on('payments', 'payout_status_change', onPayoutStatusChange);
        return () => {
            sockets.off('payments', 'payout_status_change', onPayoutStatusChange);
        }
    }

    useEffect(() => {
        fetchTransfers();
        let disconnect = connectToSockets();
        return disconnect;
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}>
            {getContent()}
        </Layer>
    )
}

export const processInstructionRejection = ({ abstract, error, setLoading, utils }) => {

    const onDetachFromSubscription = () => {
        utils.alert.show({
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onDetachFromSubscriptionConfirm, 500);
                    return;
                }
            },
            message: `Are you sure that you want to detach the "${error.data?.instruction.name}" instruction from your subscription? This will not delete your instruction from your profile.`,
            title: 'Detach Instruction'
        });
    }

    const onDetachFromSubscriptionConfirm = async () => {
        try {

            // set loading flag and submit request to server
            onSetLoading(true);
            await Request.post(utils, '/subscriptions/', {
                id: error.data?.instruction.id,
                subscription_id: error.data?.subscription_id,
                type: 'detach_instruction'
            });

            // notify subscribers of data change
            utils.content.fetch(['trigger_instructions', 'subscriptions']);

            // end loading and show confirmation alert
            onSetLoading(false)
            utils.banner.show({
                title: 'Instruction Detached',
                message: `The instruction "${abstract.object.name}" has been detached and will no longer be automatically triggered.`
            });

        } catch(e) {
            onSetLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue detaching this instruction from your subscription. ${e.message || 'An unknown error occurred'}`,
                origin: 'process_instruction_rejection.detach_from_subscription'
            });
        }
    }

    const onSetLoading = val => {
        typeof(setLoading) === 'function' && setLoading(val);
    }

    // prepare default description for alert
    let description = `You'll need to upgrade your subscription if you wish to save these changes`;
    if(error.data?.instruction) {
        description = `You'll need to detach this instruction from your subscription or upgrade your subscription if you wish to save these changes`;
    }

    // show alert with details about error and options to remedy
    utils.alert.show({
        buttons: [{
            key: 'upgrade',
            title: 'Upgrade Subscription', 
            style: 'default'
        },{
            key: 'detach',
            title: 'Detach from Subscription',
            style: 'destructive',
            visible: error.data?.instruction ? true : false
        },{
            key: 'cancel',
            title: 'Cancel',
            style: 'cancel'
        }],
        onClick: key => {
            if(key === 'upgrade') {
                utils.layer.open({
                    Component: NewSubscription.bind(this, {}),
                    id: 'new_subscription'
                });
                return;
            }
            if(key === 'detach') {
                setTimeout(onDetachFromSubscription, 500);
                return;
            }
        },
        message: `${error.message}. ${description}.`,
        title: 'Submission Rejected'
    });
}

const PromotionsManager = ({ description, isPartnershipRequest = false, onChange, promotions = [], utils }) => {

    const [loading, setLoading] = useState(false);

    const onAddCodeClick = () => {
        let code = null;
        utils.alert.show({
            title: 'Add Discount Code',
            message: `If you've been given a discount code, go ahead and enter it below to apply your savings. No code? No worries—most users don't have one. Just leave the field blank and continue without it.`,
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    onChange={text => code = text}
                    placeholder={'Discount Code'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Done',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(code && key === 'confirm') {
                    setLoading(true);
                    setTimeout(onValidatePromotionCode.bind(this, code), 500);
                    return;
                }
            }
        });
    }

    const onRemoveClick = id => {
        utils.alert.show({
            title: 'Remove Promotion',
            message: 'Are you sure that you want to remove this promotion? This may change the overall cost of this subscription.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    let next = promotions.filter(promotion => promotion.id !== id);
                    if(typeof(onChange) === 'function') {
                        onChange(next);
                    }
                    return;
                }
            }
        });
    }

    const onValidatePromotionCode = async code => {
        try {

            // submit request to server
            let { promotion } = await Request.post(utils, '/subscriptions/', {
                code,
                is_partnership_request: isPartnershipRequest,
                type: 'validate_promotional_code'
            });

            // end loading and notify subscribers of data change
            setLoading(false);
            if(typeof(onChange) === 'function') {

                // return single promotion is promotion does not play with others
                if(promotion.preferences?.allows_additional_promotions === false) {
                    onChange([promotion]);
                    return;
                }

                // add promotion to list of promotions and return
                let next = update(promotions, { $push: [promotion ]});
                onChange(next);
            }

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue validating your promotional code. ${e.message || 'An unknown error occurred'}`,
                origin: 'promotions_manager.on_validate_promotion_code'
            });
        }
    }

    const getAdditionComponents = () => {

        // determine if additional promotions can be added
        let canAddPromotions = promotions.find(promotion => promotion.preferences?.allows_additional_promotions === false) ? false : true;
        return canAddPromotions === true && (
            <div 
            className={`view-entry ${window.theme}`}
            onClick={onAddCodeClick}
            style={{
                alignItems: 'center',
                borderTop: promotions.length > 0 && Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'row',
                padding: 10,
                width: '100%',
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: Appearance.colors.text(),
                    flexGrow: 1
                }}>{'Add Discount Code'}</span>
                <img
                src={'images/next-arrow-grey-small.png'}
                style={{
                    height: 12,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 12
                }} />
            </div>
        )
    }

    const getContent = () => {
        if(loading === true) {
            return (
                <LayerItem 
                collapsed={false}
                title={'Promotions'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: 15,
                        width: '100%',
                    }}>
                        <LottieView
                        autoPlay={true}
                        loop={true}
                        source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                        style={{
                            height: 45,
                            width: 45
                        }}/>
                    </div>
                </LayerItem>
            )
        }
        return (
            <LayerItem 
            collapsed={false}
            title={'Promotions'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    {typeof(description) === 'string' && (
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            borderBottom: Appearance.styles.bottomBorder(),
                            display: 'block',
                            padding: '12px 16px 12px 16px'
                        }}>{description}</span>
                    )}
                    {promotions.map((promotion, index) => {
                        return (
                            <div
                            key={index}
                            style={{
                                alignItems: 'center',
                                borderBottom: index !== promotions.length - 1 && Appearance.styles.bottomBorder(),
                                display: 'flex',
                                flexDirection: 'row',
                                padding: 10,
                                width: '100%',
                            }}>
                                <img 
                                src={window.theme === 'dark' ? 'images/promotion-dark.png' : 'images/promotion-light.png'}
                                style={{
                                    height: 35,
                                    marginRight: 8,
                                    objectFit: 'contain',
                                    width: 35
                                }} />
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.subTitle(),
                                        color: Appearance.colors.text()
                                    }}>{promotion.title || 'Untitled'}</span>
                                    <span style={{
                                        ...Appearance.textStyles.subTitle()
                                    }}>{Subscription.promotions.overview(promotion)}</span>
                                </div>
                                <AltBadge 
                                content={{
                                    color: Appearance.colors.red,
                                    text: 'Remove'
                                }} 
                                onClick={onRemoveClick.bind(this, promotion.id)} />
                            </div>
                        )
                    })}
                    {getAdditionComponents()}
                </div>
            </LayerItem>
        )
    }

    return getContent();
}

export const Subscriptions = ({ utils }) => {

    const ref = useRef(null);
    const [loading, setLoading] = useState(false);
    const { requiem, setRequiem } = useDataStore();
    const [subscriptions, setSubscriptions] = useState([]);

    const onNewSubscriptionClick = () => {
        utils.layer.open({
            Component: NewSubscription.bind(this, {}),
            id: 'new_subscription'
        });
    }

     const onProcessRequiemNavigationRequest = (options = {}) => {

        // no further action is needed if there is no target match
        if(requiem.action?.target !== 'billing') {
            return;
        }

        // determine next steps for the action request
        switch(requiem.action?.key) {
            case 'subscriptions':
            setTimeout(() => {
                window.scrollTo({
                    behavior: "smooth",
                    left: 0,
                    top: ref.current?.offsetTop
                });
            }, 1500);
            break;  
            
            case 'subscription_details':
            if(!options.subscriptions) {
                return;
            }
            onSubscriptionClick(options.subscriptions[0]);
            break;
        }

        // reset requiem object for next request
        setRequiem({ ...requiem, action: null });
    }

    const onSubscriptionClick = subscription => {
        utils.layer.open({
            abstract: Abstract.create({
                object: subscription,
                type: 'subscriptions'
            }),
            Component: SubscriptionDetails,
            id: `subscription_details_${subscription.id}`
        });
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'new_subscription',
            onClick: onNewSubscriptionClick,
            text: 'Explore Plan Options'
        }];
    }

    const getCostLabel = subscription => {
        if(subscription.costs?.total === 0) {
            return 'No Cost';
        }
        return `${Utils.toCurrency(subscription.costs?.total)}/${Subscription.schedules.toText(subscription.schedule)}`
    }
    
    const getSubscriptions = () => {
        return subscriptions.map((subscription, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onSubscriptionClick.bind(this, subscription)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== subscriptions.length - 1 && Appearance.styles.bottomBorder(),
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payment-method-dark.png' : 'images/payment-method-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{`${subscription.tier.title} Subscription`.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText()
                        }}>{`Renews ${Utils.format.date(subscription.renewal_date, { exclude_time: true })}`}</span>
                    </div>
                    {subscription.pause_until_date !== null && (
                        <AltBadge 
                        className={'d-none d-xl-flex'}
                        content={{
                            color: Appearance.colors.blue,
                            text: 'Paused'
                        }} />
                    )}
                    {subscription.active === true && (
                        <AltBadge 
                        className={'d-none d-xl-flex'}
                        content={{
                            color: subscription.costs?.total === 0 ? Appearance.colors.gold : Appearance.colors.primary(),
                            text: getCostLabel(subscription)
                        }} />
                    )}
                    {subscription.active === false && (
                        <AltBadge 
                        className={'d-none d-xl-flex'}
                        content={{
                            color: Appearance.colors.red,
                            text: 'Inactive'
                        }} />
                    )}
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 20,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            let { subscriptions } = await Request.get(utils, '/subscriptions/', {
                type: 'list'
            });

            // prepare targets for local state update and requiem processing
            let targets = subscriptions.map(subscription => Subscription.create(subscription));
            setSubscriptions(targets);

            // end loading and submit targets for requiem processing
            setLoading(false);
            onProcessRequiemNavigationRequest({ subscriptions: targets });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your subscriptions list. ${e.message || 'An unknown error occurred'}`,
                origin: 'promotions_manager.fetch_subscriptions',
            });
        }
    }
    
    useEffect(() => {
        onProcessRequiemNavigationRequest();
    }, [requiem]);

    useEffect(() => {
        fetchSubscriptions();
        utils.content.subscribe('subscriptions_list', ['subscriptions'], {
            onFetch: fetchSubscriptions,
            onUpdate: abstract => {
                setSubscriptions(subscriptions => {
                    return subscriptions.map(subscription => {
                        return subscription.id === abstract.getID() ? abstract.object : subscription;
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe('subscriptions_list');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Below you'll find a list of all subscriptions associated with your account, including key details such as billing cycles, renewal dates, and renewal costs. You can easily add, cancel, or update any of your subscriptions by clicking on them.`}
                buttons={loading === false && subscriptions.length === 0 ? getContainerButtons() : null}
                loading={loading}
                title={'Subscriptions'}>
                    {subscriptions.length > 0 && (
                        <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            {getSubscriptions()}
                        </div>
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const SubscriptionDetails = ({ abstract, index, options, utils }) => {

    const layerID = `subscription_details_${abstract.getID()}`;
    const [childPlans, setChildPlans] = useState([]);
    const [costs, setCosts] = useState({});
    const [edits, setEdits] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [promotions, setPromotions] = useState([]);
    const [subscription, setSubscription] = useState(abstract.object);

    const onAddNewFamilyPlanClick = () => {

        let emailAddress = null;
        utils.alert.show({
            title: 'Add Family Member',
            message: `We'll send an email invitation to the address that you provide below. Once they accept the invitation, they will be added to your family plan and will begin receiving benefits as a family member.`,
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    onChange={text => emailAddress = text}
                    placeholder={'Email Address'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Send Invitation',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onAddNewFamilyPlanConfirm.bind(this, emailAddress), 500);
                    return;
                }
            }
        });
    }

    const onAddNewFamilyPlanConfirm = async emailAddress => {
        try {

            setLoading('child_plans');
            let { invitation } = await Request.post(utils, '/subscriptions/', {
                email_address: emailAddress,
                id: abstract.getID(),
                type: 'new_family_plan_member'
            });

            // end loading and add invitation to list of child plans
            setLoading(false);
            setChildPlans(childPlans => {
                return update(childPlans, {
                    $push: [{
                        ...invitation,
                        type: 'invitation'
                    }]
                });
            });

            // show confirmation alert
            utils.alert.show({
                title: 'Invitation Sent',
                message: `An invitation has been sent to ${emailAddress}. Once they accept the invitation, they will be added to your family plan and will gain access to all services associated with your subscription.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue adding this user to your family plan. ${e.message || 'An unknown error occurred'}`,
                origin: 'subscription_details.add_new_family_plan_confirm'
            });
        }
    }

    const onCancelChildPlanInvitation = invitation => {
        utils.alert.show({
            title: 'Cancel Invitation',
            message: `Are you sure that you want to cancel this invitation? This will prevent the user from joining your family plan unless you send them a new invitation.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onCancelChildPlanInvitationConfirm.bind(this, invitation), 500);
                    return;
                }
            }
        });
    }

    const onCancelChildPlanInvitationConfirm = async invitation => {
        try {

            setLoading('child_plans');
            await Request.post(utils, '/subscriptions/', {
                invitation_id: invitation.id,
                subscription_id: abstract.getID(),
                type: 'cancel_child_plan_invitation',
            });

            // remove invitation from list of child plans
            setChildPlans(childPlans.filter(entry => entry.id !== invitation.id));

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Invitation Cancelled',
                message: `The invitation for ${invitation.email_address} has been cancelled. The user will no longer be able to join your family plan unless you send them a new invitation.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue cancelling this invitation. ${e.message || 'An unknown error occurred'}`,
                origin: 'subscription_details.cancel_child_plan_invitation_confirm'
            });
        }
    }

    const onCancelSubscription = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {
        
                    // submit request to server
                    await Request.post(utils, '/subscriptions/', {
                        id: abstract.getID(),
                        type: 'cancel'
                    });

                    // update local target and notify subscribers of data change
                    abstract.object.active = false;
                    setSubscription(abstract.object);

                    // notify subscribers of data change
                    utils.content.fetch('subscriptions');

                    // end loading and notify origin that promise has been resolved
                    resolve({ 
                        message: 'This subscription has been cancelled and all services associated with it have been deactivated.',
                        onClick: setLayerState.bind(this, 'close') 
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue cancelling your subscription. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Cancel Subscription',
                message: 'Are you sure that you want to cancel your subscription? This will deactivate all services associated with your account.',
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onConfigureLifeChecks = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('life_checks', 'create');

            // show layer with options to configure triggers
            utils.layer.open({
                abstract: abstract,
                Component: ConfigureTriggers.bind(this, {
                    category: 'life_checks',
                    onChange: preferences => {
                        abstract.object.preferences = preferences;
                        setSubscription(abstract.object);
                    }
                }),
                id: `life_checks.${abstract.getID()}`
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onConfigureEmergencyTriggers = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('emergency_triggers', 'create');

            // show layer with options to configure triggers
            utils.layer.open({
                abstract: abstract,
                Component: ConfigureTriggers.bind(this, {
                    category: 'emergency_triggers',
                    onChange: preferences => {
                        abstract.object.preferences = preferences;
                        setSubscription(abstract.object);
                    }
                }),
                id: `emergency_triggers.${abstract.getID()}`
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onPauseSubscription = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async days => {
                try {

                    // prepare date for pause expiration
                    let date = moment().add(days, 'days');
        
                    // submit request to server
                    await Request.post(utils, '/subscriptions/', {
                        date: date.utc().format('YYYY-MM-DD'),
                        id: abstract.getID(),
                        type: 'pause'
                    });
        
                    // update target and notify subscribers of data change
                    abstract.object.pause_until_date = date;
                    utils.content.update(abstract);

                    // end loading and notify origin that promise has been resolved
                    resolve({ message: `This subscription has been paused for ${days} ${days === 1 ? 'day' : 'days'} and will automatically resume once that time is up.` });
        
                } catch(e) {
                    let error = new Error(`There was an issue pausing your subscription. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let days = 0;
            utils.alert.show({
                title: 'Pause Subscription',
                message: 'How long would you like to pause your subscription for? Pausing a subscription does not pause the payments associated with your plan.',
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <ListField
                        items={[...new Array(30)].map((_, index) => ({
                            id: index + 1,
                            title: `${index + 1} ${index + 1 === 1 ? 'day' : 'days'}`
                        }))} 
                        onChange={item => days = item?.id} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(days > 0 && key === 'confirm') {
                        setTimeout(onConfirm.bind(this, days), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onPromotionsChange = targets => {
        setPromotions(targets);
        setEdits(edits => [...edits, 'promotions']);
    }

    const onRemoveChildPlan = plan => {
        utils.alert.show({
            title: 'Remove Family Member',
            message: `Are you sure that you want to remove ${plan.user?.full_name || 'this user'} from your family plan? This will not delete their account, but it will remove all services associated with your subscription and will deactivate their subscription.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onRemoveChildPlanConfirm.bind(this, plan), 500);
                    return;
                }
            }
        });
    }

    const onRemoveChildPlanConfirm = async plan => {
        try {

            setLoading('child_plans');
            await Request.post(utils, '/subscriptions/', {
                child_plan_id: plan.id,
                subscription_id: abstract.getID(),
                type: 'remove_child_plan'
            });

            // remove child plan from list of child plans
            setChildPlans(childPlans.filter(entry => entry.id !== plan.id));

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'All Done!',
                message: `${plan.user?.full_name || 'The user'} has been removed from your family plan.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue removing this user from your family plan. ${e.message || 'An unknown error occurred'}`,
                origin: 'subscription_details.remove_child_plan_confirm'
            });
        }
    }

    const onResumeSubscription = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async days => {
                try {

                    // submit request to server
                    await Request.post(utils, '/subscriptions/', {
                        id: abstract.getID(),
                        type: 'resume'
                    });
        
                    // update target and notify subscribers of data change
                    abstract.object.pause_until_date = null;
                    utils.content.update(abstract);

                    // end loading and notify origin that promise has been resolved
                    resolve({ message: `This subscription has been resumed and is immediately available for service.` });
        
                } catch(e) {
                    let error = new Error(`There was an issue resuming your subscription. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Resume Subscription',
                message: 'Are you sure that you want to resume your subscription?',
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onUpgradeSubscription = () => {

        // close current layer 
        setLayerState('close');

        // open layer for subscription selection
        utils.layer.open({
            Component: NewSubscription.bind(this, {}),
            id: 'new_subscription'
        });
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            setLoading('submit');

            // verify payment method change if applicable
            if(edits.includes('payment_method') === true && paymentMethod?.id !== subscription.payment_method_id) {
                await verifyPaymentMethodChange();
            }

            // update promotions if applicable
            if(edits.includes('promotions') === true) {
                await Request.post(utils, '/subscriptions/', {
                    id: abstract.getID(),
                    promotions: promotions.map(promotion => promotion.code),
                    type: 'update_promotions'
                });
            }

            // set loading flag and submit request to server
            await Request.post(utils, '/subscriptions/', {
                id: abstract.getID(),
                payment_method_id: paymentMethod?.id,
                type: 'update'
            });

            // update target and notify subscribers of data change
            abstract.object.costs = costs;
            abstract.object.payment_method_id = paymentMethod?.id;
            abstract.object.preferences.promotions = promotions;
            utils.content.update(abstract);

            // end loading and show confirmation alert
            setLoading(false)
            utils.banner.show({
                title: 'Subscription Updated',
                message: 'The changes to your subscription have been saved.',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue updating your subscription. ${e.message || 'An unknown error occurred'}`,
                origin: 'subscription_details.on_submit_click'
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit',
            visible: edits.length > 0
        }];
    }

    const getChildPlans = () => {
        return (
            <LayerItem 
            collapsed={false}
            title={'Family Plan Members'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    position: 'relative',
                    width: '100%'
                }}>
                    {childPlans?.map((entry, index) => {
                        return (
                            <div
                            key={index}
                            style={{
                                alignItems: 'center',
                                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                                display: 'flex',
                                flexDirection: 'row',
                                padding: 10,
                                width: '100%',
                            }}>
                                <img 
                                src={entry.user?.avatar || 'images/user-placeholder.png'}
                                style={{
                                    borderRadius: 17.5,
                                    height: 35,
                                    marginRight: 8,
                                    objectFit: 'cover',
                                    width: 35
                                }} />
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.subTitle(),
                                        color: Appearance.colors.text()
                                    }}>{entry.type === 'invitation' ? entry.email_address : entry.user.full_name}</span>
                                    <span style={{
                                        ...Appearance.textStyles.subTitle()
                                    }}>{entry.type === 'invitation' ? `Invitation Sent: ${Utils.format.date(entry.date)}` : `${entry.tier.title} Subscription`}</span>
                                </div>
                                <AltBadge 
                                content={{
                                    color: Appearance.colors.red,
                                    text: entry.type === 'invitation' ? 'Cancel' : 'Remove'
                                }} 
                                onClick={entry.type === 'invitation' ? onCancelChildPlanInvitation.bind(this, entry) : onRemoveChildPlan.bind(this, entry)} />
                            </div>
                        )
                    })}
                    <div 
                    className={`view-entry ${window.theme}`}
                    onClick={onAddNewFamilyPlanClick}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        padding: 10,
                        width: '100%',
                    }}>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            color: Appearance.colors.text(),
                            flexGrow: 1
                        }}>{'Add Family Member'}</span>
                        <img
                        src={'images/next-arrow-grey-small.png'}
                        style={{
                            height: 12,
                            marginLeft: 8,
                            objectFit: 'contain',
                            opacity: 0.75,
                            width: 12
                        }} />
                    </div>
                    {loading === 'child_plans' && (
                        <div style={{
                            borderRadius: 2,
                            bottom: 0,
                            height: 2,
                            left: 0,
                            overflow: 'hidden',
                            position: 'absolute',
                            right: 0,
                            width: '100%'
                        }}>
                            <ProgressBar/>
                        </div>
                    )}
                </div>
            </LayerItem>
        )
    }

    const getGeneralLineItems = () => {

        // prepare default line items for billing breakdown
        let items = [{
            key: 'schedule',
            title: 'Processing Cycle',
            value: Subscription.schedules.toText(subscription.schedule)
        },{
            key: 'renewal_date',
            title: 'Renewal Date',
            value: Utils.format.date(subscription.renewal_date, { exclude_time: true })
        },{
            key: 'base_cost',
            title: 'Subscription Cost',
            value: Utils.toCurrency(costs?.base ?? 0)
        }];

        // determine if at least one promotion is present
        if(costs?.promotions?.length > 0) {

            // add available promotions for service prior to sub-total
            costs.promotions.forEach(promotion => {
                if(promotion.category === Subscription.promotions.categories.get().service) {
                    items.push({
                        key: promotion.id,
                        title: promotion.title,
                        value: Utils.toCurrency(promotion.amount)
                    });
                }
            });

            // add sub-total line items 
            items.push({
                key: 'sub_total',
                title: 'Sub-Total',
                value: Utils.toCurrency(costs.sub_total ?? 0)
            });

            // add available promotions for discount after sub-total
            costs.promotions.forEach(promotion => {
                if(promotion.category !== Subscription.promotions.categories.get().service) {
                    items.push({
                        color: Appearance.colors.gold,
                        key: promotion.id,
                        title: `${promotion.title} Promotion`,
                        value: `-${Utils.toCurrency(promotion.amount)}`
                    });
                }
            });
        }

        // add total item to list of items
        items.push({
            key: 'total',
            title: 'Total Cost',
            value: Utils.toCurrency(costs?.total ?? 0)
        });
        
        // prepare sections for mapper
        let fields = [{
            key: 'benefits',
            title: 'Plan Benefits',
            items: subscription.tier.benefits.filter(benefit => {
                return benefit.value === null || benefit.value === false ? false : true;
            }).map((benefit, index) => {
                return {
                    key: index,
                    title: benefit.title,
                    value: benefit.value
                }
            })
        },{
            collapsed: false,
            key: 'details',
            lastItem: false,
            title: 'Costs and Billing',
            items: items
        }];

        return (
            <FieldMapper
            sections={fields}
            utils={utils} />
        )
    }

    const getPaymentMethods = () => {
        return (
            <LayerItem 
            collapsed={false}
            title={'Payment Method'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <PaymentMethodSelector
                    defaultID={subscription.payment_method_id}
                    onChange={setPaymentMethod}
                    schedule={subscription.schedule}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getPreferenceComponents = () => {

        if(!subscription) {
            return null;
        }

        let items = [{
            description: 'This lets you control whether Emergency Triggers are enabled and what actions should take place when activated.',
            image: subscription.preferences.emergency_triggers.enabled ? 'images/configure-emergency-triggers-red.png' : 'images/configure-emergency-triggers-grey.png',
            onClick: onConfigureEmergencyTriggers,
            title: 'Emergency Trigger'
        },{
            description: 'This gives you control around when LifeChecks are sent and how we should react.',
            image: subscription.preferences.life_checks.enabled ? 'images/configure-lifechecks-green.png' : 'images/configure-lifechecks-grey.png',
            onClick: onConfigureLifeChecks,
            title: 'LifeChecks'
        },{
            description: 'To unlock higher account limits and additional features, you can upgrade your subscription at any time.',
            image: 'images/upgrade-subscription.png',
            onClick: onUpgradeSubscription,
            title: 'Upgrade Subscription',
            visible: subscription.flags?.includes('upgrade_available')
        },{
            description: 'This will temporarily pause the subscription for up to 30 days if you need to unplug.',
            image: 'images/pause-subscription-blue.png',
            onAsyncClick: onPauseSubscription,
            title: 'Pause Subscription',
            visible: subscription.pause_until_date === null
        },{
            description: 'This will let you immediately resume service for this subscription.',
            image: 'images/resume-subscription-green.png',
            onAsyncClick: onResumeSubscription,
            title: 'Resume Subscription',
            visible: subscription.pause_until_date !== null
        },{
            description: 'This will end all services associated with your subscription and cancel all future payments.',
            image: 'images/delete-red-small.png',
            onAsyncClick: onCancelSubscription,
            title: 'Cancel Subscription',
            visible: subscription.active === true
        }];

        return (
            <LayerItem 
            collapsed={false}
            title={'Preferences'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {items.map((item, index) => {
                        if(item.visible === false) {
                            return null;
                        }
                        return (
                            <AsyncInteractionItem 
                            key={index}
                            description={item.description}
                            image={item.image}
                            lastItem={index === items.length - 1}
                            onClick={item.onClick}
                            onAsyncClick={item.onAsyncClick}
                            title={item.title}
                            utils={utils} />
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getPromotions = () => {
        if(loading === 'promotions') {
            return (
                <LayerItem 
                collapsed={false}
                title={'Promotions'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: 15,
                        width: '100%',
                    }}>
                        <LottieView
                        autoPlay={true}
                        loop={true}
                        source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                        style={{
                            height: 45,
                            width: 45
                        }}/>
                    </div>
                </LayerItem>
            )
        }
        return (
            <PromotionsManager 
            onChange={onPromotionsChange}
            promotions={promotions}
            utils={utils} />
        )
    }

    const getRegistrationDetails = () => {

        let items = [{
            collapsed: false,
            key: 'registration',
            lastItem: false,
            title: 'Registration and Details',
            items: [{
                key: 'created',
                title: 'Created',
                value: Utils.format.date(subscription.created)
            },{
                key: 'id',
                title: 'ID',
                value: `SUB-${subscription.id}`
            },{
                color: Appearance.colors.red,
                key: 'pause_until_date',
                title: 'Paused Until',
                value: subscription.pause_until_date && Utils.format.date(subscription.pause_until_date, { exclude_time: true }),
                visible: subscription.pause_until_date ? true : false
            },{
                key: 'title',
                title: 'Tier',
                value: subscription.tier.title
            }]
        }];

        return (
            <FieldMapper
            sections={items}
            utils={utils} />
        )
    }

    const fetchDynamicCosts = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { costs } = await Request.get(utils, '/subscriptions/', {
                id: subscription.id,
                promotions: promotions.map(promotion => promotion.code),
                schedule: subscription.schedule,
                type: 'get_dynamic_costs'
            });

            // end loading and update local state with dynamic costs
            setLoading(false);
            setCosts(costs);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your customized subscription cost. ${e.message || 'An unknown error occurred'}`,
                origin: 'subscription_details.fetch_dynamic_costs'
            });
        }
    }

    const setupTarget = async () => {
        try {

            // set costs state using data from target
            setCosts(abstract.object.costs || {});

            // set loading flag and submit request to server
            setLoading(true);
            let { child_plans, promotions } = await Request.get(utils, '/subscriptions/', {
                id: subscription.id,
                type: 'get_resources'
            });

            // end loading and update local state with dynamic costs
            setLoading(false);
            setChildPlans(child_plans);
            setPromotions(promotions);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue preparing the resources for this subscription. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close'),
                origin: 'subscription_details.setup_target'
            });
        }
    }

    const verifyPaymentMethodChange = () => {
        return new Promise((resolve, reject) => {
            utils.alert.show({
                title: 'Update Payment Method',
                message: 'Are you sure that you want to change the payment method for this subscription?',
                buttons: [{
                    key: 'confirm',
                    title: 'Configure Subscription',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Close',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        resolve();
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }
    
    useEffect(() => {
        if(paymentMethod && paymentMethod?.id !== subscription.payment_method_id) {
            setEdits(edits => [...edits, 'payment_method']);
        }
    }, [paymentMethod]);

    useEffect(() => {
        if(edits.includes('promotions') === true) {
            fetchDynamicCosts();
        }
    }, [edits, promotions]);

    useEffect(() => {

        setupTarget();
        utils.content.subscribe(layerID, ['subscriptions'], {
            onUpdate: next => {
                if(next.getID() === abstract.getID()) {
                    setSubscription(next.object);
                }
            }
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}>
            {getRegistrationDetails()}
            {getGeneralLineItems()}
            {getChildPlans()}
            {getPromotions()}
            {getPaymentMethods()}
            {getPreferenceComponents()}
        </Layer>
    )
}

export const SubscriptionOptions = ({ embedded = false, isActivationMode = false, minimal = false, onSelectionClick, showStarterPlan = true, utils }) => {

    // isActivationMode controls presentation context:
    // false = shopping mode (default) - includes toggle, savings copy, "From $X" pricing
    // true = activation mode - removes friction, fixed monthly pricing, emphasis on execution

    const [activationPlanDetailsExpanded, setActivationPlanDetailsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [tiers, setTiers] = useState([]);
    const { request, system, user } = useDataStore();

    const onBenefitDescriptionClick = benefit => {

        // log analytics event
        utils.analytics.log('view_benefit_description', { benefit_key: benefit.key });

        // show alert with benefit description
        utils.alert.show({
            title: benefit.title,
            message: benefit.description
        });
    }
    
    const onNewSubscription = tier => {

        // notify subscribers that a selection has been made if applicable
        if(typeof(onSelectionClick) === 'function') {
            onSelectionClick(tier, schedule);
            return;
        }

        // open layer to configure and purchase subscription
        utils.layer.open({
            Component: NewSubscription.bind(this, { 
                defaultSchedule: schedule,
                freeTrial: true, 
                selection: tier 
            }),
            id: 'new_subscription'
        });
    }

    const onTierSelectionClick = tier => {
        
        // determine if sales mode is active
        if(request.mode === 'sales') {
            utils.layer.open({
                Component: SalesOpsOrderForm.bind(this, {
                    tier: tier,
                    schedule: schedule,
                }),
                id: 'sales_ops_order_form'
            });
            return;
        }

        // determine if a user needs to sign in before proceeding
        if(!user) {

            // request login if activation mode is enabled
            if(isActivationMode === true) {
                utils.user.request.login({ shouldRedirectUser: false }, onNewSubscription.bind(this, tier));
                return;
            }
            
            // request signup for new users
            utils.user.request.signup({ onComplete: onNewSubscription.bind(this, tier) });
            return;
        }

        // track firebase recommended begin_checkout event
        utils.analytics.log('begin_checkout', {
            currency: 'USD',
            items: [{
                item_category: 'Subscription',
                item_id: tier.id,
                item_name: tier.title,
                origin: 'subscription_options',
                quantity: 1
            }]
        });

        // track subscription signup started
        utils.analytics.log('subscription_signup_started', {
            tier_id: tier.id,
            tier_name: tier.title
        });

        // present options to configure and purchase subscription
        onNewSubscription(tier);
    }

    const getActivationCollapseComponents = () => {
        return isActivationMode === true && (
            <div 
            className={`view-entry ${window.theme}`}
            onClick={setActivationPlanDetailsExpanded.bind(this, !activationPlanDetailsExpanded)}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                opacity: 0.35,
                padding: 12,
                textAlign: 'center',
                width: '100%'
            }}>
                <span style={{
                    ...Appearance.textStyles.defaultTextSmall(),
                    marginRight: 8
                }}>{`${activationPlanDetailsExpanded ? 'Hide' : 'View'} Plan Details`}</span>
            </div>
        )
    }

    const getBenefits = tier => {

        // prepare list of benefit targets
        let targets = tier.benefits.filter(benefit => benefit.value === null || benefit.value === false ? false : true);
        
        // determine if a bi-fold approach is needed for activation mode
        if(isActivationMode === true) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    {targets.filter(benefit => {
                        return activationPlanDetailsExpanded === true || benefit.metadata?.is_activation_benefit === true ? true : false;
                    }).map(getBenefitComponent)}
                </div>
            );
        }

        // fallback to showing all benefits in shopping mode
        return targets.map(getBenefitComponent);
    }

    const getBenefitComponent = (benefit, index) => {
        return (
            <div 
            className={`view-entry ${window.theme}`}
            onClick={onBenefitDescriptionClick.bind(this, benefit)}
            key={index}
            style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                padding: 12,
                textAlign: 'left',
                width: '100%'
            }}>
                <img 
                src={window.theme === 'dark' ? 'images/asterick-badge-dark.png' : 'images/asterick-badge-light.png'}
                style={{
                    height: 25,
                    marginRight: 8,
                    objectFit: 'contain',
                    width: 25 
                }} />
                <span style={{
                    ...Appearance.textStyles.defaultTextSmall(),
                    flexGrow: 1
                }}>
                    {`${benefit.title}: `}
                    <span style={{
                        ...['Priority','Unlimited','Premium'].includes(benefit.value) && {
                            color: Appearance.colors.primary(),
                            fontWeight:  'bold' 
                        }
                    }}>{benefit.value}</span>
                </span>
                <img
                src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                style={{
                    height: 12,
                    objectFit: 'contain',
                    opacity: 0.25,
                    width: 12
                }} />
            </div>
        )
    }

    const getContent = () => {
        if(minimal === true) {
            return (
                <>
                {request.mode === 'sales' && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        paddingBottom: 24,
                        width: '100%'
                    }}>
                        {getMultiYearTermSelector()}
                    </div>
                )}
                {getContentComponents()}
                </>
            )
        }
        return (
            <div className={embedded ? 'pt-0 pt-xl-3' : ''}>
                {getHeaderContent()}
                <div 
                className={embedded ? 'p-0 mt-4' : 'p-4 p-xl-0 mt-0 mt-xl-4'}
                style={{
                    width: '100%'
                }}>
                    {getContentComponents()}
                </div>
            </div>
        )
    }

    const getContentComponents = () => {
        if(loading === true) {
            return (
                <div 
                className={'mt-0 mt-xl-4'}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    border: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 16
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(tiers.length === 0) {
            return (
                <div 
                className={`p-4 p-xl-5 mt-0 mt-xl-4`}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    border: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: system.window.isMobile ? 'center' : 'left'
                }}>
                    <img 
                    src={'images/red-x-icon.png'}
                    style={{
                        height: 50,
                        marginBottom: 8,
                        width: 50
                    }} />
                    <span style={{
                        ...Appearance.textStyles.heavyHeader(),
                        fontFamily: 'Zen Dots'
                    }}>{'No Tiers Found'.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        whiteSpace: 'normal'
                    }}>{'An unknown error occurred while loading the available of subscription tiers'}</span>
                </div>
            )
        }
        if(Utils.isMobile() === true) {
            return tiers.map((tier, index) => {
                return (
                    <div 
                    className={index === 0 ? 'mt-0' : 'mt-3 mt-xl-4'}
                    key={index}
                    style={{
                        ...Appearance.styles.unstyledPanel(),
                        ...embedded === false && { border: 'none' },
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%'
                    }}>
                        <CollapseContainer 
                        defaultValue={true} 
                        header={(
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '16px 20px 16px 20px',
                                textAlign: 'left',
                                width: '100%'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.heavyHeader(),
                                    color: Appearance.colors.primary(),
                                    fontFamily: 'Zen Dots',
                                    marginBottom: 0
                                }}>{tier.title.toUpperCase()}</span>
                                <span style={{
                                    color: Appearance.colors.text(),
                                    fontFamily: 'Zen Dots',
                                    fontSize: 12,
                                    whiteSpace: 'normal'
                                }}>{getCostLabel(tier).toUpperCase()}</span>
                                {isActivationMode === false && getDiscounts(tier)}
                                {isActivationMode === true && getComparitiveLabel(tier)}
                            </div>
                        )}>
                            <div style={{
                                borderTop: `1px solid ${Appearance.colors.divider()}`,
                                flexGrow: 1,
                                height: '100%',
                                width: '100%'
                            }}>
                                {getBenefits(tier)}
                            </div>
                            {getActivationCollapseComponents()}
                            {getTierSelectionButtons(tier)}
                        </CollapseContainer>
                    </div>
                )
            });
        }
        
        return (
            <div 
            className={'row mx-0 mt-0 flex-nowrap'}
            style={{
                width: '100%',
                ...embedded === false && minimal === false && {
                    maxWidth: 1500
                }
            }}>
                {tiers.map((tier, index) => {
                    return (
                        <div 
                        className={`col-12 col-md p-0 p-xl-2`}
                        key={index}>
                            <div style={{
                                ...Appearance.styles.unstyledPanel(),
                                ...embedded === false && minimal === false && { border: 'none' },
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                width: '100%'
                            }}>
                                <div style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: 24,
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.heavyHeader(),
                                        color: Appearance.colors.primary(),
                                        fontFamily: 'Zen Dots',
                                        marginBottom: 0
                                    }}>{tier.title.toUpperCase()}</span>
                                    <span style={{
                                        color: Appearance.colors.text(),
                                        fontFamily: 'Zen Dots',
                                        fontSize: 12,
                                        whiteSpace: 'normal'
                                    }}>{getCostLabel(tier).toUpperCase()}</span>
                                    {isActivationMode === false && getDiscounts(tier)}
                                    {isActivationMode === true && getComparitiveLabel(tier)}
                                </div>
                                <div style={{
                                    borderTop: `1px solid ${Appearance.colors.divider()}`,
                                    flexGrow: 1,
                                    height: '100%',
                                    width: '100%'
                                }}>
                                    {getBenefits(tier)}
                                </div>
                                {getActivationCollapseComponents()}
                                {getTierSelectionButtons(tier)}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    const getComparitiveLabel = tier => {
        return (
            <span style={{
                color: Appearance.colors.primary(),
                fontSize: 13,
                fontWeight: 600, 
                marginTop: 12
            }}>{tier.preferences.marketing.comparitive_language}</span>
        )
    }

    const getCostLabel = tier => {

        // check if selected schedule has a cost
        let target = tier.costs[schedule];
        if(target.total === 0) {
            return 'Free for Everyone';
        }

        // determine if sales mode is active 
        if(request.mode === 'sales') {
            return `From ${Utils.toCurrency(target.total)}`;
        }

        // in activation mode, show clear single price without any other language
        if(isActivationMode) {
            return target ? `${Utils.toCurrency(target.total)}/${target.descriptor}` : 'Cost Not Available';
        }

        // fallback to showing standard pricing format since schedule can be adjusted
        return target ? `From ${Utils.toCurrency(target.total)}/${target.descriptor}` : 'Cost Not Available';
    }

    const getDiscounts = tier => {

        let target = tier.costs[schedule];

        // determine if sales mode is active
        if(request.mode === 'sales') {
            return null;
        }

        // create a flag to determine if component is shown, we hide instead of remove for the starter plan to preserve visual consistency
        let shouldShowDiscount = tier.metadata.is_starter_plan ? false : true;
        return target?.discount > 0 && (
            <div style={{
                display: 'flex',
                marginTop: 8,
                opacity: shouldShowDiscount ? 1 : 0
            }}>
                <AltBadge content={{
                    color: Appearance.colors.green,
                    text: `Save ${(target.discount * 100).toFixed(0)}% By Paying ${target.title}`
                }} 
                style={{
                    marginLeft: 0 
                }} />
            </div>
        )
    }

    const getHeaderContent = () => {
        if(isActivationMode === true) {
            return (
                <PanelContentContainer
                body={`Choose how much protection you want your plan to provide once it's active. All plans activate your LifeWipe account. The difference is how much protection runs automatically and how frequently LifeChecks occur. Click on any plan benefit to learn more about what it includes.`}
                containerStyle={{
                    padding: 0
                }}
                title={'Activate Your Plan'} 
                style={{
                    maxWidth: 1500
                }} />
            )
        }
        if(request.mode === 'sales') {
            return (
                <PanelContentContainer
                body={styles => (
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                    }}>
                        <span style={styles.textStyles}>{`Digital protection isn't something you set up for a few months. It's infrastructure—something that should be in place for as long as you have accounts, files, and people who depend on you. That's why we offer multi-year plans with significant savings. You lock in your rate, secure your protection, and never worry about renewal reminders or price increases.`}</span>
                        {getMultiYearTermSelector()}
                    </div>
                )}
                containerStyle={embedded === true && {
                    padding: 0
                }}
                title={'Choose Your Plan'} 
                style={{
                    maxWidth: 1500
                }} />
            )
        }
        return (
            <PanelContentContainer
            body={styles => (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span style={styles.textStyles}>{`All plans include our core digital legacy protection features — from secure credential storage and emergency data wipes to legacy messaging and trusted contact transfers — ensuring your digital life is protected, even when you're not around to protect it. `}<b>{`Pro Tip: `}</b>{`Pay annually to save more and skip the monthly billing. You’ll lock in a better rate and ensure uninterrupted protection for your digital legacy.`}</span>
                    <BoolToggle
                    enabled={'Annually'}
                    disabled={'Monthly'}
                    isEnabled={schedule === 6}
                    onChange={val => setSchedule(val ? 6 : 4)}
                    style={{
                        alignSelf: 'center',
                        marginTop: 24,
                        maxWidth: 350
                    }}/>
                </div>
            )}
            containerStyle={embedded === true && {
                padding: 0
            }}
            title={'Choose Your Plan'} 
            style={{
                maxWidth: 1500
            }} />
        )
    }

    const getMultiYearTermSelector = () => {

        // prepare list of multi-year schedule options
        let schedules = Subscription.schedules.get();
        let multiYearSchedules = [schedules.three_year, schedules.five_year, schedules.seven_year, schedules.ten_year];
        let options = Object.values(multiYearSchedules).map(code => ({
            label: Subscription.schedules.toText(code),
            value: code
        }));

        return (
            <MultiToggle
            options={options}
            selectedIndex={options.length - 1}
            onChange={option => setSchedule(option.value)}
            style={{
                alignSelf: 'center',
                maxWidth: 750,
                ...minimal === false && {
                    marginTop: 24
                }
            }} />
        )
    }

    const getTierSelectionButtons = tier => {
        if(request.mode === 'sales' || request.focus === 'third_party_referral') {
            return (
                <Button
                color={'primary'}
                containerStyle={{
                    width: '100%'
                }}
                label={'Get Started'}
                loading={loading === tier.id}
                onClick={onTierSelectionClick.bind(this, tier)}
                style={{
                    borderRadius: 0
                }} 
                type={'large'} />
            )
        }
        if(isActivationMode === true) {
            return (
                <Button
                color={'primary'}
                containerStyle={{
                    width: '100%'
                }}
                label={'Activate This Plan'}
                loading={loading === tier.id}
                onClick={onTierSelectionClick.bind(this, tier)}
                style={{
                    borderRadius: 0
                }} 
                type={'large'} />
            )
        }

        return (
            <Button
            color={'primary'}
            label={tier.metadata.is_starter_plan === true ? 'Get Started' : 'Start Free Trial'}
            loading={loading === tier.id}
            onClick={onTierSelectionClick.bind(this, tier)}
            style={{
                borderRadius: 0
            }} 
            type={'large'} />
        )
    }

    const fetchSubscriptionTiers = async () => {
        try {
            setLoading(true);
            let { tiers } = await Request.get(utils, '/subscriptions/', {
                include_starter_plan: shouldShowStarterPlan(),
                type: 'available_tiers'
            });

            // update local state with available tiers and end loading
            setTiers(tiers);
            setLoading(false);

        } catch(e) {
            setLoading(false);
            console.error(e.message);
        }
    }

    const shouldShowStarterPlan = () => {   
        return request.mode === 'sales' ? false : showStarterPlan;
    }

    useEffect(() => {
        setSchedule(request.mode === 'sales' ? Subscription.schedules.get().ten_year : Subscription.schedules.get().monthly);
        fetchSubscriptionTiers();
    }, []);

    return (
        <div 
        className={'text-left text-xl-center'}
        style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        }}>
            {getContent()}
        </div>
    )
}

export const SubscriptionPlanSelectionQuiz = ({ onChange }, { index, options, utils }) => {

    const layerID = 'subscription_plan_selection_quiz';
    const [activeIndex, setActiveIndex] = useState(null);
    const [contentAnimations, contentAnimationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -10
    }));
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progressBarAnimations, progressBarAnimationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        width: '0%',
    }));
    const [selectedTier, setSelectedTier] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [values, setValues] = useState([]);

    const questions = [{
        title: "Device Usage",
        description: "How many personal devices do you use daily? We protect phones, tablets, and laptops. Pick the option that matches your digital lifestyle.",
        options: [{
            label: "Just one — my phone or laptop.",
            value: 'bronze'
        },{
            label: "A few — phone, laptop, maybe a tablet too.",
            value: 'silver'
        },{
            label: "Multiple — I use several devices for work and life.",
            value: 'gold'
        }]
    },{
        title: "Digital Beneficiaries",
        description: "Do you want your digital assets to go to multiple people or just one? Legacy transfers allow you to send data or messages to others after death.",
        options: [{
            label: "Just one person is fine.",
            value: 'bronze'
        },{
            label: "A couple of key people.",
            value: 'silver'
        },{
            label: "I want to assign different things to different people.",
            value: 'gold'
        }]
    },{
        title: "LifeCheck Frequency",
        description: "How often should LifeWipe check on your status? We'll ping you to confirm you're okay — if you stop responding, actions can begin.",
        options: [{
            label: "Every few months is enough.",
            value: 'bronze'
        },{
            label: "Monthly is better for peace of mind.",
            value: 'silver'
        },{
            label: "I want full control — weekly, custom dates, etc.",
            value: 'gold'
        }]
    },{
        title: "Emergency Triggers",
        description: "How many emergency wipes or panic button uses do you need each month? These are serious actions — limit or expand based on your risk level.",
        options: [{
            label: "Just 1 emergency trigger per month is enough.",
            value: 'bronze'
        },{
            label: "I’d like 2 per month in case I travel or feel at risk.",
            value: 'silver'
        },{
            label: "I want maximum flexibility — up to 5 triggers.",
            value: 'gold'
        }]
    },{
        title: "External Accounts",
        description: "How many accounts (email, storage, crypto, etc.) do you want to secure or wipe? Link accounts to protect, transfer, or wipe them if something happens.",
        options: [{
            label: "Up to 5 accounts is plenty.",
            value: 'bronze'
        },{
            label: "I’ll probably connect around 10.",
            value: 'silver'
        },{
            label: "I want full coverage — 20+ accounts.",
            value: 'gold'
        }]
    }];

    const onBackClick = () => {

        // animate progress bar before changing active index
        progressBarAnimationsAPI.start({ width: `${parseInt((activeIndex - 1) / questions.length * 100)}%` });

        // request next active index
        onRequestNewActiveIndex(activeIndex - 1);
    }

    const onCheckboxChange = (index, value, evt) => {
        evt.stopPropagation();
        setValues(values => {
            return update(values, {
                [activeIndex]: {
                    $set: value ? index : null
                }
            });
        });
    }

    const onExplorePlanClick = () => {

        // open new layer for subscription selection
        utils.layer.open({
            Component: NewSubscription.bind(this, {
                freeTrial: true,
                onSelectionComplete: result => {
                    setLayerState('close');
                    onChange(result);
                },
                selection: selectedTier 
            }),
            id: 'new_subscription'
        });
    }

    const onNextClick = () => {

        // verify that an answer has been provided for the active question
        if(values[activeIndex] === null || values[activeIndex] === undefined) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'Please make a selection before moving on'
            });
            return;
        }

        // animate progress bar before changing active index
        progressBarAnimationsAPI.start({ width: `${parseInt((activeIndex + 1) / questions.length * 100)}%` });

        // request next active index
        onRequestNewActiveIndex(activeIndex + 1);
    }

    const onRequestNewActiveIndex = async index => {

        // animate all components out of view 
        await setContentAnimations('out');

        // set next active index so new content can be prepared
        setActiveIndex(index);

        // animate components back into view
        await setContentAnimations('in');
    }

    const onSubmitClick = async () => {

        // animate progress bar before changing active index
        progressBarAnimationsAPI.start({ width: '100%' });
        
        // animate all components out of view 
        await setContentAnimations('out');

        // determine score based on selected values
        let scores = values.reduce((object, value, activeIndex) => {
            let option = questions[activeIndex].options[value];
            object[option.value].value++;
            return object;
        }, {
            bronze: { id: 1, value: 0 },
            silver: { id: 2, value: 0 },
            gold: { id: 3, value: 0 }
        });

        // determine which subsription received the highest score
        let selection = Object.values(scores).reduce((selection, entry) => {
            if(!selection || selection.value <= entry.value) {
                return entry;
            }
            return selection;
        });

        // attempt to match selectionID up with the full tier object
        let tier = tiers.find(tier => tier.id === selection?.id);

        // update local state with selected plan id
        setSelectedTier(tier);

        // animate components back into view
        await setContentAnimations('in');
    }

    const onViewAllPlans = () => {
        utils.layer.open({
            Component: NewSubscription.bind(this, {
                freeTrial: true,
                onSelectionComplete: result => {
                    setLayerState('close');
                    onChange(result);
                }
            }),
            id: 'new_subscription'
        });
    }

    const getButtons = () => {
        if(loading === true) {
            return null;
        }
        if(selectedTier) {
            return [{
                color: 'dark',
                key: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            },{
                color: 'primary',
                key: 'view_all',
                onClick: onViewAllPlans,
                text: 'View All Plans'
            }];
        }
        return [{
            color: 'dark',
            key: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close',
            visible: activeIndex === 0
        },{
            color: 'dark',
            key: 'back',
            onClick: onBackClick,
            text: 'Back',
            visible: activeIndex !== 0
        },{
            color: 'primary',
            key: 'next',
            onClick: activeIndex === questions.length - 1 ? onSubmitClick : onNextClick,
            text: 'Next'
        }];
    }

    const getComponents = () => {

        // determine if content is still loading
        if(loading === true || activeIndex === null) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 125,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }

        // show content explaining the selected tier if applciable
        if(selectedTier) {
            return (
                <animated.div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    width: '100%',
                    ...contentAnimations
                }}>
                    <div style={{
                        alignItems: 'center',
                        backgroundColor: Appearance.colors.divider(),
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        marginBottom: 24, 
                        padding: Utils.isMobile() ? 36 : 72,
                        width: '100%'
                    }}>
                        <img
                        className={'float'}
                        src={getImageForTier()}
                        style={{
                            height: 125,
                            objectFit: 'contain',
                            width: 125
                        }} />
                    </div>
                    <span style={{
                        ...Appearance.textStyles.heavyHeader(),
                        fontFamily: 'Zen Dots',
                        marginBottom: 8
                    }}>{`${selectedTier.title} Subscription`.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`Great news — we've matched you with the LifeWipe plan that fits you best! Take a moment to explore what's included, like messaging tools, emergency actions, and how many accounts and devices you can protect. If it feels right, you're all set to activate — and if not, you can always switch plans later.`}</span>
                </animated.div>
            )
        }

        // prepare content using the active question
        let entry = questions[activeIndex];
        return (
            <animated.div style={{
                position: 'relative',
                width: '100%',
                ...contentAnimations
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                title={entry.title}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        marginBottom: 18
                    }}>{entry.description}</span>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {entry.options.map((option, index, options) => {
                            return (
                                <div
                                className={`view-entry ${window.theme}`}
                                key={index}
                                onClick={onCheckboxChange.bind(this, index, true)}
                                style={{
                                    alignItems: 'center',
                                    borderBottom: index !== options.length - 1 && Appearance.styles.bottomBorder(),
                                    display: 'flex',
                                    flexDirection: 'row',
                                    padding: 16,
                                    width: '100%'
                                }}>
                                    <Checkbox 
                                    checked={values[activeIndex] === index}
                                    onChange={onCheckboxChange.bind(this, index)} />
                                    <span 
                                    className={'cursor-pointer'}
                                    style={{
                                        ...Appearance.textStyles.defaultText(),
                                        marginLeft: 12
                                    }}>{option.label}</span>
                                </div>
                            )
                        })}
                    </div>
                </LayerItem>
            </animated.div>
        )
    }

    const getImageForTier = () => {
        switch(selectedTier?.id) {
            case 1:
            return 'images/subscription-quiz-selection-bronze.png';

            case 2:
            return 'images/subscription-quiz-selection-silver.png';

            case 3:
            return 'images/subscription-quiz-selection-gold.png';

            case 4:
            return 'images/subscription-quiz-selection-elite.png';
        }
    }

    const getProgressBar = () => {
        if(loading === true || selectedTier) {
            return null;
        }
        return (
            <div style={{
                backgroundColor: Appearance.colors.divider(),
                borderRadius: 2.5,
                height: 5,
                marginBottom: 16,
                position: 'relative'
            }}>
                <animated.div style={{
                    backgroundColor: Appearance.colors.primary(),
                    borderRadius: 2.5,
                    height: 5,
                    ...progressBarAnimations
                }} />
            </div>
        )
    }

    const fetchSubscriptionTiers = async () => {
        try {
            setLoading(true);
            let { tiers } = await Request.get(utils, '/subscriptions/', {
                type: 'available_tiers'
            });

            setLoading(false);
            setTiers(tiers);
            onRequestNewActiveIndex(0);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue preparing the content for this quiz. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close'),
                origin: 'subscription_plan_signup_quiz.fetch_subscription_tiers'
            });
        }
    }

    const setContentAnimations = direction => {
        return new Promise(resolve => {
            contentAnimationsAPI.start({
                to: async next => {
                    await next({
                        opacity: direction === 'in' ? 1 : 0,
                        top: direction === 'in' ? 0 : -10,
                    });
                    resolve();
                } 
            });
        });
    }

    useEffect(() => {
        fetchSubscriptionTiers();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}
        utils={utils}>
            {getProgressBar()}
            {getComponents()}
        </Layer>
    )
}